import { useEffect, useState, useCallback, useMemo } from 'react';
import {
  Box, Typography, Paper, Chip,
  Skeleton, Alert, Collapse,
  IconButton, Divider, Tooltip, ButtonBase, useMediaQuery, Menu, MenuItem, ListSubheader, CircularProgress, Stack,
} from '@mui/material';
import { useTheme, alpha } from '@mui/material/styles';
import { Global, css } from '@emotion/react';
import { ChevronDown, Info, Hourglass, Check, ChartNoAxesCombined, ArrowUpRight, ArrowRight, Target, Zap, ChevronRight, CalendarDays, Triangle, AlarmClock, FileText, Download, MessageSquareText } from 'lucide-react';
import ReportBooklet from './ReportBooklet';
import CustomDialogSmall from '../../../components/Dialog/DiaCompSmall';
import CustomDialog from '../../../components/Dialog/DiaCompMedium';
import MarkerTrendChart from '../Bloodmarker/ReuseComps/MarkerTrendChart';
import TextInfoComp from '../Bloodmarker/ReuseComps/TextInfoComp';
import { API_URL, WEIGHTDATA, BPLIST } from '../../../constants';
import { getAddonPurchased } from '../../../Auth/auth';
import { SUBSYSTEM_KEY } from '../Bloodtests/bloodtestKeys';
import axios from 'axios';
import { PieChart, Pie, Cell, ResponsiveContainer } from 'recharts';
import { useAuth } from '../../../provider';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from '../../../language';
import ScrollX from '../../../components/Scroll/ScrollX';

// ---------------------------------------------------------------------------
// Global: font + keyframe animations
// ---------------------------------------------------------------------------
const GLOBAL_STYLES = css`
  @keyframes hc-fade-up {
    from { opacity: 0; transform: translateY(20px); }
    to   { opacity: 1; transform: translateY(0);    }
  }
  @keyframes hc-scale-in {
    from { opacity: 0; transform: scale(0.96); }
    to   { opacity: 1; transform: scale(1);    }
  }
  @keyframes hc-meter-in {
    from { clip-path: inset(0 100% 0 0); }
    to   { clip-path: inset(0 0 0 0);    }
  }
`;

// ---------------------------------------------------------------------------
// Design tokens derived from MUI theme
// ---------------------------------------------------------------------------
function useTokens() {
  const theme = useTheme();
  const p     = theme.palette;
  const dark  = p.mode === 'dark';

  const green = p.traffic.green;
  const amber = p.traffic.orange;
  const red   = p.traffic.red;

  const riskAlphaBg     = dark ? 0.15 : 0.08;
  const riskAlphaBorder = dark ? 0.40 : 0.30;

  const T = {
    dark,
    pageBg:       p.background.default,
    cardBg:       p.background.paper,
    cardBorder:   dark ? p.greydark.C500  : p.greydark.C50,
    cardShadow:   dark
      ? '0 2px 20px rgba(0,0,0,0.5)'
      : '0 2px 14px rgba(0,0,0,0.07)',
    sectionLine:  dark ? p.greydark.C600  : p.grey.C200,
    heading:      dark ? p.grey.C50       : p.greydark.C900,
    arrow:        dark ? p.grey.C400      : p.greydark.C500,
    body:         dark ? p.grey.C600      : p.greydark.C500,
    bodyStrong:   dark ? p.grey.C300      : p.greydark.C700,
    overline:     dark ? p.grey.C800      : p.grey.C700,

    chipBg:       dark ? p.greydark.C700  : p.grey.C100,
    chipText:     dark ? p.grey.C700      : p.greydark.C500,
    accentBg:     dark ? alpha(p.quartiary.C500, 0.15) : p.quartiary.C50,
    accentBorder: dark ? p.quartiary.C700  : p.quartiary.C200,
    accentText:   dark ? p.quartiary.C300  : p.quartiary.C700,
    green,
    greenBg:      alpha(green, riskAlphaBg),
    greenBorder:  alpha(green, riskAlphaBorder),
    amber,
    amberBg:      alpha(amber, riskAlphaBg),
    amberBorder:  alpha(amber, riskAlphaBorder),
    red,
    redBg:        alpha(red, riskAlphaBg),
    redBorder:    alpha(red, riskAlphaBorder),
  };

  const RISK_CONFIG = {
    low:  { dot: T.green, bg: T.greenBg, border: T.greenBorder, label: 'Optimal'  },
    med:  { dot: T.amber, bg: T.amberBg, border: T.amberBorder, label: 'Moderate' },
    high: { dot: T.red,   bg: T.redBg,   border: T.redBorder,   label: 'Elevated' },
  };

  const PRIORITY_CONFIG = {
    high:   { dot: T.red,   bg: T.redBg,   border: T.redBorder,   label: 'High Priority'   },
    medium: { dot: T.amber, bg: T.amberBg, border: T.amberBorder, label: 'Medium Priority'  },
    low:    { dot: T.green, bg: T.greenBg, border: T.greenBorder, label: 'Low Priority'     },
  };

  return { T, RISK_CONFIG, PRIORITY_CONFIG };
}

// ---------------------------------------------------------------------------
// Static constants
// ---------------------------------------------------------------------------
const PILLAR_META = {
  'Metabolism': {
    route:    '/MetabolicHealthWelcome',
    labelKey: 'systemMetabolic',
    infoKey:  'hc_pillar_metabolic_info',
  },
  'Immune System': {
    route:    '/ImmuneHealthWelcome',
    labelKey: 'systemImmune',
    infoKey:  'hc_pillar_immune_info',
  },
  'Organ Function': {
    route:    '/OrganHealthWelcome',
    labelKey: 'systemOrgan',
    infoKey:  'hc_pillar_organ_info',
  },
  'Nutrients and Vitamins': {
    route:    '/NutrientHealthWelcome',
    labelKey: 'systemNutrient',
    infoKey:  'hc_pillar_nutrient_info',
  },
};

const MECHANISM_TITLE_KEYS = {
  'Insulin resistance':                                                                    'mechanism_insulin_resistance',
  'Atherosclerosis risk':                                                                  'mechanism_atherosclerosis_risk',
  'Energy production':                                                                     'mechanism_energy_production',
  'Metabolic inflammation':                                                                'mechanism_metabolic_inflammation',
  'Chronic low-grade inflammation':                                                        'mechanism_chronic_inflammation',
  'Immune surveillance and cell balance':                                                  'mechanism_immune_cell_balance',
  'Hematologic integrity and oxygen transport':                                            'mechanism_hematologic_integrity',
  'Hepatic function and fatty liver risk':                                                 'mechanism_hepatic_function',
  'Renal function and microvascular health':                                               'mechanism_renal_function',
  'Tissue damage and metabolic by-products':                                               'mechanism_tissue_damage',
  'Iron balance, anemia, and oxidative stress':                                            'mechanism_iron_balance',
  'Vitamin D–magnesium axis and cardiometabolic–immune health':                           'mechanism_vitamin_d_magnesium',
  'Electrolyte and mineral balance, vascular calcification, and neuromuscular function':   'mechanism_electrolyte_balance',
};

// Info-dialog content per mechanism, mirroring the per-pillar HealthSystems
// pages (MetabolicHealth.jsx, ImmuneHealth.jsx, OrganHealth.jsx,
// Micronutrientshealth.jsx) so the same explanations show up here.
const MECHANISM_INFO_KEYS = {
  'Insulin resistance':                                                                    'mechanism_insulin_info',
  'Atherosclerosis risk':                                                                  'mechanism_athero_info',
  'Energy production':                                                                     'mechanism_energy_info',
  'Metabolic inflammation':                                                                'mechanism_metabolic_inflammation_info',
  'Chronic low-grade inflammation':                                                        'mechanism_chronic_info',
  'Immune surveillance and cell balance':                                                  'mechanism_immune_cell_info',
  'Hematologic integrity and oxygen transport':                                            'mechanism_hematologic_info',
  'Hepatic function and fatty liver risk':                                                 'mechanism_hepatic_info',
  'Renal function and microvascular health':                                               'mechanism_renal_info',
  'Tissue damage and metabolic by-products':                                               'mechanism_tissue_info',
  'Iron balance, anemia, and oxidative stress':                                            'mechanism_iron_info',
  'Vitamin D–magnesium axis and cardiometabolic–immune health':                           'mechanism_vitamind_info',
  'Electrolyte and mineral balance, vascular calcification, and neuromuscular function':   'mechanism_electrolyte_info',
};

const MECHANISM_MARKER_SHORTS = {
  'Insulin resistance':      ['Glu', 'A1c', 'Tri', 'Hdl', 'TriHdlRatio', 'Ins', 'Qu', 'Homa', 'Mets', 'INDEX3'],
  'Atherosclerosis risk':    ['Ldl', 'Hdl', 'TcHdlRatio', 'NonHdlChol', 'LdlHdlRatio', 'TriHdlRatio', 'Apo', 'Crp', 'Lpa', 'INDEX3'],
  'Energy production':       ['Magn', 'Ferr', 'Iron', 'B12', 'B1', 'B2', 'B9', 'A1c', 'Glu', 'Crp', 'Tsh', 'Q10', 'Ins', 'INDEX3'],
  'Metabolic inflammation':  ['Crp', 'Tri', 'Glu', 'A1c', 'Ins', 'Homa', 'Hdl', 'INDEX3'],
  'Chronic low-grade inflammation':             ['Crp', 'Sedi', 'NLR', 'SII', 'PLR', 'MPVPLT'],
  'Immune surveillance and cell balance':       ['NLR', 'SII', 'Leuc', 'Neutra', 'Lympha', 'Monoa', 'Eosa', 'Basoa', 'PLR'],
  'Hematologic integrity and oxygen transport': ['Hemo', 'Rdw', 'Eryt', 'Hct', 'Mcv', 'Mch', 'Mchc', 'Throm'],
  'Hepatic function and fatty liver risk':   ['Alt', 'Ast', 'AstAltRatio', 'Ggt', 'GGTHDLRatio', 'Fib4', 'Bill', 'Alp', 'Alba', 'RATIO63', 'AERATIO'],
  'Renal function and microvascular health': ['CreaGfr', 'Crea', 'Bun', 'BunCreaRatio'],
  'Tissue damage and metabolic by-products': ['Uric', 'Cre', 'Ldh', 'Lip', 'PanAmy', 'Test', 'Dhea', 'Ctx', 'Ost', 'Pth', 'Tsh', 'T3', 'T4', 'INDEX3'],
  'Iron balance, anemia, and oxidative stress':                                          ['Ferr', 'Iron', 'TrFerr', 'TrFerrSat', 'TrFerrTibco'],
  'Vitamin D–magnesium axis and cardiometabolic–immune health':               ['D3', 'Magn', 'B12', 'K2', 'K1', 'B9', 'B6', 'B2', 'B1', 'B7', 'A', 'C', 'E', 'Caro'],
  'Electrolyte and mineral balance, vascular calcification, and neuromuscular function': ['Nat', 'Kal', 'NatKal', 'Calc', 'Phos', 'Chlor', 'CalcPhos', 'Magn', 'Se', 'Cu', 'Zinc'],
};

// New-format reports (see API_report.md) send a flat `mechanisms` list instead
// of `pillar_summaries`. M1–M3 belong to pillar 1, M4–M6 to pillar 2, and so
// on; the position also picks the legacy mechanism name, which is what the
// info texts and marker lists above are keyed by.
// Reports created from this day on carry `markers` / `markers_attention` per
// mechanism; older ones keep using MECHANISM_MARKER_SHORTS.
const API_MARKERS_SINCE = '2026-10-01';

const PILLAR_ORDER = ['Metabolism', 'Immune System', 'Organ Function', 'Nutrients and Vitamins'];

const MECHANISM_BY_NUMBER = [
  'Insulin resistance',
  'Atherosclerosis risk',
  'Energy production',
  'Chronic low-grade inflammation',
  'Immune surveillance and cell balance',
  'Hematologic integrity and oxygen transport',
  'Hepatic function and fatty liver risk',
  'Renal function and microvascular health',
  'Tissue damage and metabolic by-products',
  'Iron balance, anemia, and oxidative stress',
  'Vitamin D–magnesium axis and cardiometabolic–immune health',
  'Electrolyte and mineral balance, vascular calcification, and neuromuscular function',
];

function buildPillars(report) {
  const mechanisms = report?.mechanisms ?? [];
  if (!mechanisms.length) return report?.pillar_summaries ?? [];
  const pillars = PILLAR_ORDER.map((subsystem) => ({ subsystem, mechanism_scores: [] }));
  const apiMarkers = String(report.created_at ?? '').slice(0, 10) >= API_MARKERS_SINCE;
  mechanisms.forEach((m, i) => {
    const n = parseInt(/^M(\d+)/.exec(m.id ?? '')?.[1], 10) || i + 1;
    pillars[Math.floor((n - 1) / 3)]?.mechanism_scores.push({
      id:             m.id,
      mechanism:      MECHANISM_BY_NUMBER[n - 1],
      title:          m.title,
      short_description: m.short_description,
      risk_score:     m.score,
      interpretation: m.text,
      ...(apiMarkers
        ? { markers: m.markers ?? [], markers_attention: m.markers_attention ?? [] }
        : {}),
    });
  });
  return pillars.filter((pl) => pl.mechanism_scores.length > 0);
}

function mechLookup(map, name) {
  if (map[name] !== undefined) return map[name];
  const lower = (name ?? '').toLowerCase();
  const key = Object.keys(map).find(k => k.toLowerCase() === lower);
  return key !== undefined ? map[key] : undefined;
}

function getRiskLevel(score) {
  if (score <= 3) return 'low';
  if (score <= 6) return 'med';
  return 'high';
}

// ---------------------------------------------------------------------------
// DateSelector — lab visit date picker on the report pages (also used on the
// BioAge page): the date opens every visit grouped by year.
// `dates` arrive newest first.
// ---------------------------------------------------------------------------
export function DateSelector({ dates, selectedDate, onSelect }) {
  const { T } = useTokens();
  const theme = useTheme();
  const { t, language } = useTranslation();
  const [anchor, setAnchor] = useState(null);
  const locale = language === 'de' ? 'de-DE' : 'en-GB';

  const idx = dates.indexOf(selectedDate);
  const multiple = dates.length > 1;

  const toDate = (d) => new Date(`${d}T00:00:00`);
  const rtf = new Intl.RelativeTimeFormat(locale, { numeric: 'auto' });
  const relative = (d) => {
    const days = Math.round((toDate(d) - new Date().setHours(0, 0, 0, 0)) / 86400000);
    if (Math.abs(days) < 31) return rtf.format(days, 'day');
    const months = Math.round(days / 30.44);
    if (Math.abs(months) < 24) return rtf.format(months, 'month');
    return rtf.format(Math.round(months / 12), 'year');
  };
  // [[year, [dates…]], …] — newest year first, like `dates`
  const byYear = dates.reduce((acc, d) => {
    const y = d.slice(0, 4);
    if (acc.at(-1)?.[0] !== y) acc.push([y, []]);
    acc.at(-1)[1].push(d);
    return acc;
  }, []);

  const select = (d) => { setAnchor(null); if (d !== selectedDate) onSelect(d); };

  if (!selectedDate) return null;

  return (
    <>
      {/* The selected date — opens the list of all visits */}
      <ButtonBase
        onClick={multiple ? (e) => setAnchor(e.currentTarget) : undefined}
        disabled={!multiple}
        aria-haspopup={multiple ? 'listbox' : undefined}
        aria-expanded={multiple ? Boolean(anchor) : undefined}
        aria-label={`${t('hc_stats_visit_date')}: ${toDate(selectedDate).toLocaleDateString(locale, { day: 'numeric', month: 'long', year: 'numeric' })}`}
        sx={{
          minWidth: 0, maxWidth: '100%', gap: 1.25, pl: 1.75, pr: multiple ? 1.25 : 2, py: 0.875,
          borderRadius: '16px', textAlign: 'left', justifyContent: 'flex-start',
          bgcolor: theme.palette.greydark.C900, color: '#fff',
          border: T.dark ? '1px solid rgba(255,255,255,0.14)' : 'none',
          transition: `filter 150ms ease, transform 160ms ${EASE_OUT}`,
          '@media (hover: hover) and (pointer: fine)': { '&:hover': { filter: 'brightness(1.2)' } },
          '&:active': { transform: 'scale(0.97)' },
          '&.Mui-focusVisible': { outline: `2px solid ${theme.palette.greydark.C900}`, outlineOffset: 2 },
          '&.Mui-disabled': { color: '#fff' },
        }}
      >
        <Box sx={{ display: 'flex', color: alpha('#fff', 0.7), flexShrink: 0 }}>
          <CalendarDays size={20} strokeWidth={2.25} />
        </Box>
        <Box sx={{ minWidth: 0 }}>
          <Typography component="time" dateTime={selectedDate} sx={{
            display: 'block', fontSize: { xs: '0.98rem', sm: '1.05rem' }, fontWeight: 600, lineHeight: 1.25,
            color: '#fff', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap',
          }}>
            {toDate(selectedDate).toLocaleDateString(locale, { day: 'numeric', month: 'long', year: 'numeric' })}
          </Typography>
          <Typography sx={{ fontSize: '0.78rem', lineHeight: 1.3, color: alpha('#fff', 0.68), whiteSpace: 'nowrap' }}>
            {idx === 0 ? t('hc_date_latest') : relative(selectedDate)}
          </Typography>
        </Box>
        {multiple && (
          <Box sx={{
            display: 'flex', ml: 0.5, flexShrink: 0, color: alpha('#fff', 0.8),
            transform: anchor ? 'rotate(180deg)' : 'none',
            transition: `transform 200ms ${EASE_OUT}`,
          }}>
            <ChevronDown size={18} strokeWidth={2.5} />
          </Box>
        )}
      </ButtonBase>

      {/* All visits, grouped by year */}
      <Menu
        anchorEl={anchor}
        open={Boolean(anchor)}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        transformOrigin={{ vertical: 'top', horizontal: 'center' }}
        transitionDuration={{ enter: 180, exit: 120 }}
        slotProps={{
          paper: { sx: {
            mt: 1, borderRadius: '18px', minWidth: Math.max(anchor?.offsetWidth ?? 0, 280), maxHeight: 440,
            bgcolor: T.dark ? theme.palette.greydark.C800 : theme.palette.background.paper,
          } },
          list: { role: 'listbox', 'aria-label': t('hc_date_all'), sx: { py: 0.75 } },
        }}
      >
        <ListSubheader disableSticky sx={{
          bgcolor: 'transparent', lineHeight: 1.4, px: 2, pt: 1, pb: 0.5,
          fontSize: '0.85rem', fontWeight: 600, color: T.heading,
        }}>
          {t('hc_date_all')} ({dates.length})
        </ListSubheader>
        {byYear.flatMap(([year, list]) => [
          <ListSubheader key={`y-${year}`} sx={{
            lineHeight: 1, px: 2, pt: 1.5, pb: 0.75,
            fontSize: '0.8rem', fontWeight: 700, color: T.body, fontVariantNumeric: 'tabular-nums',
            bgcolor: T.dark ? theme.palette.greydark.C800 : theme.palette.background.paper,
          }}>
            {year}
          </ListSubheader>,
          ...list.map((d) => {
            const active = d === selectedDate;
            const date = toDate(d);
            return (
              <MenuItem
                key={d}
                role="option"
                aria-selected={active}
                selected={active}
                onClick={() => select(d)}
                sx={{
                  mx: 0.75, px: 1.5, py: 1, gap: 2, borderRadius: '12px',
                  justifyContent: 'space-between',
                  '&.Mui-selected': { bgcolor: alpha(T.heading, T.dark ? 0.1 : 0.06) },
                  '&.Mui-selected:hover, &.Mui-selected.Mui-focusVisible': { bgcolor: alpha(T.heading, T.dark ? 0.14 : 0.09) },
                }}
              >
                <Box sx={{ minWidth: 0 }}>
                  <Typography sx={{ fontSize: '0.95rem', fontWeight: active ? 700 : 500, color: T.heading, lineHeight: 1.3, fontVariantNumeric: 'tabular-nums' }}>
                    {date.toLocaleDateString(locale, { day: 'numeric', month: 'long' })}
                  </Typography>
                  <Typography sx={{ fontSize: '0.75rem', color: T.body, lineHeight: 1.35 }}>
                    {date.toLocaleDateString(locale, { weekday: 'long' })} · {relative(d)}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}>
                  {d === dates[0] && (
                    <Box sx={{ ...CHIP_SX, bgcolor: T.accentBg, border: `1px solid ${T.accentBorder}` }}>
                      <Typography sx={{ ...CHIP_TEXT_SX, color: T.accentText }}>{t('hc_date_latest_short')}</Typography>
                    </Box>
                  )}
                  <Box sx={{ width: 18, display: 'flex', color: T.heading }}>
                    {active && <Check size={18} strokeWidth={2.5} />}
                  </Box>
                </Box>
              </MenuItem>
            );
          }),
        ])}
      </Menu>
    </>
  );
}

function DateSkeleton() {
  return (
    <Box sx={{ display: 'flex', justifyContent: 'center', mb: 4 }}>
      <Skeleton variant="rounded" width={240} height={56} sx={{ borderRadius: '16px', maxWidth: '100%' }} />
    </Box>
  );
}

// ---------------------------------------------------------------------------
// mechanismMarkers — the marker lists of one mechanism: from the report when it
// carries them (buildPillars), else from the hard-coded table + the visit values
// ---------------------------------------------------------------------------
// attention rows: { short, label, value, unit, status, countsForScore }
// evaluated chips: { short, label, value, unit, state: 'ok' | 'out' | 'flagged' }
//   'flagged' = out of range but not counted towards the score (can be harmless)
function mechanismMarkers(ms, markersMap, t) {
  const markerLabel = (short, fallback) => {
    const key = `markername_${short}`;
    return short && t(key) !== key ? t(key) : (fallback ?? short);
  };
  const isOut = (status) => !!status && String(status).toUpperCase() !== 'NORMAL';

  if (ms.markers) {
    const attention = (ms.markers_attention ?? []).map((m) => ({
      short: m.marker_short,
      label: markerLabel(m.marker_short, m.marker_name ?? m.marker),
      value: m.value, unit: m.unit, status: m.status,
      countsForScore: m.counts_for_score,
    }));
    const evaluated = ms.markers.map((m) => {
      const lab = markersMap?.[m.marker_short];
      const att = attention.find((a) => a.short === m.marker_short);
      return {
        short: m.marker_short,
        label: markerLabel(m.marker_short, m.marker_name ?? m.marker),
        value: m.value ?? lab?.value, unit: m.unit ?? lab?.unit,
        state: att ? (att.countsForScore === false ? 'flagged' : 'out') : 'ok',
      };
    });
    return { focusLabels: evaluated.map((e) => e.label), attention, evaluated };
  }

  const legacyShorts = mechLookup(MECHANISM_MARKER_SHORTS, ms.mechanism) ?? [];
  const measured = legacyShorts.filter((short) => markersMap?.[short]).map((short) => {
    const r = markersMap[short];
    return { short, label: markerLabel(short), value: r.value, unit: r.unit, status: r.status, state: isOut(r.status) ? 'out' : 'ok' };
  });
  return {
    focusLabels: legacyShorts.map((short) => markerLabel(short)),
    attention: measured.filter((m) => m.state === 'out'),
    evaluated: measured,
  };
}

// ---------------------------------------------------------------------------
// MechanismDetails — content of the DETAILS dialog: header with code, title
// and a score panel; the personalised text on the left; out-of-range markers
// and all evaluated markers on the right. Each marker opens its trend + info.
// ---------------------------------------------------------------------------
function MechanismDetails({ number, label, shortDescription, score, description, attention, evaluated, visitResults }) {
  const { T, RISK_CONFIG } = useTokens();
  const theme = useTheme();
  const { t, language } = useTranslation();
  const [chartMarker, setChartMarker] = useState(null);

  const locale = language === 'de' ? 'de-DE' : 'en-GB';
  const fmt = (v) => (v == null || v === '' || isNaN(Number(v))
    ? (v ?? '—')
    : Number(v).toLocaleString(locale, { maximumFractionDigits: 3 }));

  const level = getRiskLevel(score);
  const cfg = RISK_CONFIG[level];
  const levelLabel = t({ low: 'hc_level_low', med: 'hc_level_med', high: 'hc_level_high' }[level]);

  const counted = attention.filter((a) => a.countsForScore !== false);
  const pressSx = {
    cursor: 'pointer',
    transition: `background-color 150ms ease, border-color 150ms ease, transform 160ms ${EASE_OUT}`,
    '&:active': { transform: 'scale(0.97)' },
  };
  // Header, then body, then the marker column ease in one after another
  const enter = (i) => ({
    animation: `hc-fade-up 420ms ${EASE_OUT} ${i * 70}ms both`,
    '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
  });
  const sectionTitleSx = { fontSize: '0.95rem', fontWeight: 700, lineHeight: 1.3 };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: { xs: 3, sm: 3.5 } }}>

      {/* Header: code + title + short description | score panel */}
      <Box sx={{
        ...enter(0),
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', md: 'minmax(0, 1fr) 280px' },
        columnGap: 5, rowGap: 2.5, alignItems: 'start',
      }}>
        <Box sx={{ minWidth: 0 }}>
          <Typography sx={{ fontSize: '0.9rem', fontWeight: 600, color: T.body, fontVariantNumeric: 'tabular-nums', letterSpacing: '0.02em', mb: 0.5 }}>
            M{String(number).padStart(2, '0')}
          </Typography>
          <Typography component="h2" sx={{ fontSize: { xs: '1.5rem', sm: '1.9rem' }, fontWeight: 700, lineHeight: 1.15, color: T.heading, letterSpacing: '-0.015em' }}>
            {label}
          </Typography>
          {shortDescription && (
            <Typography sx={{ mt: 1, fontSize: { xs: '0.95rem', sm: '1.02rem' }, color: T.body, lineHeight: 1.55, maxWidth: '58ch' }}>
              {shortDescription}
            </Typography>
          )}
        </Box>

        {/* Score panel — tinted in the risk colour */}
        <Box sx={{
          p: 2, borderRadius: '18px',
          bgcolor: alpha(cfg.dot, T.dark ? 0.12 : 0.08),
          display: 'flex', flexDirection: 'column', gap: 1.5,
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1.5 }}>
            <Box>
              <Typography sx={{ fontSize: '0.8rem', color: T.body, lineHeight: 1.3 }}>{t('hc_risk_score')}</Typography>
              <Typography sx={{ fontSize: '1rem', fontWeight: 700, color: cfg.dot, lineHeight: 1.3 }}>{levelLabel}</Typography>
            </Box>
            <Typography sx={{ lineHeight: 1, color: T.heading, fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>
              <Box component="span" sx={{ fontSize: '2.25rem', fontWeight: 700, letterSpacing: '-0.02em' }}>{score}</Box>
              <Box component="span" sx={{ fontSize: '1rem', color: T.body }}>/10</Box>
            </Typography>
          </Box>
          <Box aria-hidden="true" sx={{
            display: 'grid', gridTemplateColumns: 'repeat(10, 1fr)', gap: '4px',
            animation: `hc-meter-in 700ms ${EASE_OUT} 180ms both`,
            '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
          }}>
            {Array.from({ length: 10 }).map((_, i) => (
              <Box key={i} sx={{ height: 8, borderRadius: '3px', bgcolor: i < score ? cfg.dot : alpha(T.heading, T.dark ? 0.14 : 0.1) }} />
            ))}
          </Box>
        </Box>
      </Box>

      {/* Body: text | markers */}
      <Box sx={{
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', md: 'minmax(0, 1fr) 340px' },
        columnGap: 5, rowGap: 3.5, alignItems: 'start',
      }}>
        <Typography sx={{ ...enter(1), fontSize: { xs: '1rem', sm: '1.06rem' }, color: T.bodyStrong, lineHeight: 1.8, maxWidth: '66ch' }}>
          {description}
        </Typography>

        <Box sx={{ ...enter(2), display: 'flex', flexDirection: 'column', gap: 3, minWidth: 0 }}>
          {/* Out of range (counted towards the score) */}
          {counted.length > 0 ? (
            <Box sx={{ borderRadius: '18px', bgcolor: T.redBg, px: 1.25, pt: 1.75, pb: 0.75 }}>
              <Typography component="h3" sx={{ ...sectionTitleSx, color: T.red, px: 1, mb: 0.5 }}>
                {t('hc_details_attention')} ({counted.length})
              </Typography>
              {counted.map((row, idx) => {
                const status = String(row.status ?? '').toUpperCase();
                const dir = status.includes('LOW') ? 'low' : status.includes('HIGH') ? 'high' : null;
                return (
                  <Box key={row.short ?? idx}>
                    {idx > 0 && <Divider sx={{ mx: 1, borderColor: alpha(T.red, 0.16) }} />}
                    <ButtonBase
                      onClick={() => row.short && setChartMarker(row)}
                      disabled={!row.short}
                      sx={{
                        ...pressSx, width: '100%', justifyContent: 'space-between', gap: 2, py: 1.1, px: 1,
                        borderRadius: '10px', textAlign: 'left',
                        '& .hc-chip-arrow': { transition: `transform 200ms ${EASE_OUT}` },
                        '@media (hover: hover) and (pointer: fine)': {
                          '&:hover': { bgcolor: alpha(T.red, 0.08) },
                          '&:hover .hc-chip-arrow': { transform: 'translateX(2px)' },
                        },
                        '&.Mui-focusVisible': { outline: `2px solid ${T.red}`, outlineOffset: -2 },
                      }}
                    >
                      <Typography sx={{ fontSize: '0.98rem', fontWeight: 500, color: T.heading, minWidth: 0, lineHeight: 1.35 }}>{row.label}</Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexShrink: 0, color: T.red }}>
                        <Typography sx={{ fontSize: '0.98rem', fontWeight: 700, color: T.red, fontVariantNumeric: 'tabular-nums' }}>
                          {fmt(row.value)}
                        </Typography>
                        {row.unit && (
                          <Typography sx={{ fontSize: '0.75rem', color: T.body }}>{row.unit}</Typography>
                        )}
                        {dir && (
                          <Box role="img" aria-label={t(`bt_status_${dir}`)} sx={{ display: 'flex', transform: dir === 'low' ? 'rotate(180deg)' : 'none' }}>
                            <Triangle size={12} fill="currentColor" strokeWidth={0} />
                          </Box>
                        )}
                        <Box className="hc-chip-arrow" aria-hidden="true" sx={{ display: 'flex', color: alpha(T.red, 0.6), ml: 0.25 }}>
                          <ChevronRight size={16} strokeWidth={2.5} />
                        </Box>
                      </Box>
                    </ButtonBase>
                  </Box>
                );
              })}
            </Box>
          ) : (
            <Box sx={{
              display: 'flex', alignItems: 'center', gap: 1.5, px: 2, py: 1.75,
              borderRadius: '18px', bgcolor: T.greenBg,
            }}>
              <Check size={18} strokeWidth={2.5} color={T.green} style={{ flexShrink: 0 }} />
              <Typography sx={{ color: T.green, fontWeight: 600, fontSize: '0.9rem', lineHeight: 1.5 }}>
                {t('intervention_targets_all_normal')}
              </Typography>
            </Box>
          )}

          {/* All evaluated markers as chips */}
          {evaluated.length > 0 && (
            <Box>
              <Typography component="h3" sx={{ ...sectionTitleSx, color: T.heading }}>
                {t('hc_details_evaluated')} ({evaluated.length})
              </Typography>
              <Typography sx={{ mt: 0.25, mb: 1.25, fontSize: '0.78rem', color: T.body, lineHeight: 1.4 }}>
                {t('hc_details_tap_hint')}
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                {evaluated.map((m, idx) => {
                  const out = m.state === 'out';
                  return (
                    <ButtonBase
                      key={m.short ?? idx}
                      onClick={() => m.short && setChartMarker(m)}
                      disabled={!m.short}
                      sx={{
                        ...pressSx,
                        display: 'inline-flex', alignItems: 'baseline', gap: 0.5,
                        px: 0.875, py: 0.375, borderRadius: '8px',
                        border: '1px solid', borderColor: out ? alpha(T.red, 0.4) : alpha(T.heading, T.dark ? 0.16 : 0.12),
                        bgcolor: out ? alpha(T.red, T.dark ? 0.1 : 0.05) : alpha(T.heading, T.dark ? 0.05 : 0.03),
                        '@media (hover: hover) and (pointer: fine)': {
                          '&:hover': { borderColor: out ? T.red : alpha(T.heading, 0.35) },
                        },
                        '&.Mui-focusVisible': { outline: `2px solid ${out ? T.red : T.arrow}`, outlineOffset: 2 },
                      }}
                    >
                      {!out && (
                        <Box sx={{
                          width: 7, height: 7, borderRadius: '50%', flexShrink: 0, alignSelf: 'center',
                          bgcolor: m.state === 'flagged' ? T.amber : T.green,
                        }} />
                      )}
                      <Typography component="span" sx={{ fontSize: '0.72rem', lineHeight: 1.35, fontWeight: out ? 700 : 400, color: out ? T.red : T.heading }}>
                        {m.label}
                      </Typography>
                      {m.value != null && (
                        <Typography component="span" sx={{ fontSize: '0.72rem', lineHeight: 1.35, fontWeight: 600, color: out ? T.red : T.heading, fontVariantNumeric: 'tabular-nums' }}>
                          {fmt(m.value)}
                        </Typography>
                      )}
                      {m.value != null && m.unit && (
                        <Typography component="span" sx={{ fontSize: '0.64rem', lineHeight: 1.35, color: T.body }}>{m.unit}</Typography>
                      )}
                    </ButtonBase>
                  );
                })}
              </Box>
            </Box>
          )}
        </Box>
      </Box>

      {/* Marker trend + explanation */}
      <CustomDialog
        open={!!chartMarker}
        onClose={() => setChartMarker(null)}
        title={chartMarker ? `${chartMarker.label} — ${t('bt_chart_title')}` : ''}
        content={
          chartMarker && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              <MarkerTrendChart
                markerShort={chartMarker.short}
                visitOptions={visitResults ?? []}
                userEntries={[]}
                theme={theme}
                t={t}
              />
              <TextInfoComp
                description={`description_${chartMarker.short}`}
                lowtext={`lowtext_${chartMarker.short}`}
                lowrec={`lowrec_${chartMarker.short}`}
                rangetext={`rangetext_${chartMarker.short}`}
                hightext={`hightext_${chartMarker.short}`}
                highrec={`highrec_${chartMarker.short}`}
              />
            </Box>
          )
        }
      />
    </Box>
  );
}

// ---------------------------------------------------------------------------
// MobileMechanismRow — numbered badge + data-driven dot indicator
// ---------------------------------------------------------------------------
function MobileMechanismRow({ number, name, title, shortDescription, score, description, markers, markersAttention, markersMap, visitResults, ...rest }) {
  const { T, RISK_CONFIG } = useTokens();
  const theme = useTheme();
  const { t } = useTranslation();
  const rl  = getRiskLevel(score);
  const cfg = RISK_CONFIG[rl];
  const inactiveColor = theme.palette.mode === 'dark'
    ? theme.palette.greydark.C600
    : theme.palette.grey.C300;
  const titleKey = mechLookup(MECHANISM_TITLE_KEYS, name);
  const label = title ?? (titleKey ? t(titleKey) : name);
  const infoKey = mechLookup(MECHANISM_INFO_KEYS, name);
  const { focusLabels, attention, evaluated } = mechanismMarkers(
    { mechanism: name, markers, markers_attention: markersAttention }, markersMap, t,
  );
  const [infoOpen, setInfoOpen] = useState(false);
  const [detailsOpen, setDetailsOpen] = useState(false);

  const levelLabel = t({ low: 'hc_level_low', med: 'hc_level_med', high: 'hc_level_high' }[rl]);

  return (
    <Box {...rest}
      // The whole row opens the details; the info icon inside stays its own button
      role="button"
      tabIndex={0}
      aria-label={`${label}: ${t('hc_risk_score')} ${score}/10, ${levelLabel} — ${t('hc_details')}`}
      onClick={() => setDetailsOpen(true)}
      onKeyDown={(e) => {
        if (e.target !== e.currentTarget) return;
        if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setDetailsOpen(true); }
      }}
      sx={{
      display: 'grid',
      // Phone: code | name | actions, the score block drops under the name.
      // Desktop: the score block gets its own fixed column so every row lines up.
      gridTemplateColumns: { xs: '30px minmax(0, 1fr) auto', md: '36px minmax(0, 1fr) 200px auto' },
      alignItems: 'center',
      columnGap: { xs: 1.25, md: 2.5 },
      rowGap: 1.25,
      px: { xs: 1.5, sm: 2 }, py: { xs: 1.5, sm: 1.75 },
      borderRadius: '14px',
      cursor: 'pointer',
      outline: 'none',
      transition: `background-color 150ms ease, transform 160ms ${EASE_OUT}`,
      '& .hc-row-arrow': { transition: `transform 200ms ${EASE_OUT}` },
      '@media (hover: hover) and (pointer: fine)': {
        '&:hover': { bgcolor: alpha(T.heading, T.dark ? 0.06 : 0.04) },
        '&:hover .hc-row-arrow': { transform: 'translateX(3px)' },
      },
      '&:active': { transform: 'scale(0.995)' },
      '&:focus-visible': { boxShadow: `inset 0 0 0 2px ${theme.palette.tertiary.C300}` },
    }}>
      {/* Mechanism code — quiet, the status colour lives in the score block */}
      <Typography sx={{
        alignSelf: 'start', pt: '2px',
        fontSize: { xs: '0.75rem', md: '0.8rem' }, fontWeight: 600, color: T.body,
        fontVariantNumeric: 'tabular-nums', letterSpacing: '0.02em',
      }}>
        M{String(number).padStart(2, '0')}
      </Typography>

      {/* Name + one-line explanation */}
      <Box sx={{ minWidth: 0 }}>
        <Typography sx={{ fontSize: { xs: '0.95rem', md: '1rem' }, fontWeight: 600, color: T.heading, lineHeight: 1.35 }}>
          {label}
        </Typography>
        {shortDescription && (
          <Typography sx={{
            mt: 0.25, fontSize: { xs: '0.8rem', md: '0.84rem' }, color: T.body, lineHeight: 1.5,
            display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden',
          }}>
            {shortDescription}
          </Typography>
        )}
      </Box>

      {/* Score: status word + number, then the 10-segment meter */}
      <Box sx={{
        gridColumn: { xs: 2, md: 'auto' }, gridRow: { xs: 2, md: 'auto' },
        display: 'flex', flexDirection: 'column', gap: 0.75, minWidth: 0, maxWidth: { xs: 260, md: 'none' },
      }}>
        <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 1 }}>
          <Typography sx={{ fontSize: '0.78rem', fontWeight: 600, color: cfg.dot, lineHeight: 1.2 }}>
            {levelLabel}
          </Typography>
          <Typography sx={{ lineHeight: 1, color: T.heading, fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>
            <Box component="span" sx={{ fontSize: '1.05rem', fontWeight: 700 }}>{score}</Box>
            <Box component="span" sx={{ fontSize: '0.75rem', color: T.body }}>/10</Box>
          </Typography>
        </Box>
        <Box aria-hidden="true" sx={{
          display: 'grid', gridTemplateColumns: 'repeat(10, 1fr)', gap: '3px',
          animation: `hc-meter-in 650ms ${EASE_OUT} ${120 + number * 45}ms both`,
          '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
        }}>
          {Array.from({ length: 10 }).map((_, i) => (
            <Box key={i} sx={{ height: 6, borderRadius: '2px', bgcolor: i < score ? cfg.dot : inactiveColor }} />
          ))}
        </Box>
      </Box>

      {/* Info + open details */}
      <Box sx={{
        gridColumn: { xs: 3, md: 'auto' }, gridRow: { xs: '1 / span 2', md: 'auto' },
        display: 'flex', alignItems: 'center', gap: 0.25,
      }}>
        {infoKey ? (
          <Tooltip title="Info" placement="top">
            <IconButton size="small" aria-label="Info"
              onClick={(e) => { e.stopPropagation(); setInfoOpen(true); }}
              onKeyDown={(e) => e.stopPropagation()}
              sx={{ color: T.overline }}>
              <Info size={17} strokeWidth={2.25} />
            </IconButton>
          </Tooltip>
        ) : <Box sx={{ width: 34 }} /* keeps the arrow column aligned */ />}
        <Box className="hc-row-arrow" aria-hidden="true" sx={{ display: 'flex', color: T.arrow }}>
          <ChevronRight size={20} strokeWidth={2.5} />
        </Box>
      </Box>

      <Box onClick={(e) => e.stopPropagation()} onKeyDown={(e) => e.stopPropagation()} sx={{ display: 'contents' }}>
      <CustomDialog
        open={detailsOpen}
        onClose={() => setDetailsOpen(false)}
        paperMaxWidth="1160px"
        title=""
        content={
          <MechanismDetails
            number={number}
            label={label}
            shortDescription={shortDescription}
            score={score}
            description={description}
            attention={attention}
            evaluated={evaluated}
            visitResults={visitResults}
          />
        }
      />

      {infoKey && (
        <CustomDialogSmall
          open={infoOpen}
          onClose={() => setInfoOpen(false)}
          title={label}
          content={
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              <Typography variant="subtitle1" sx={{ lineHeight: 1.5, color: T.heading }}>{t(infoKey)}</Typography>
              <Divider />
              <Box>
                <Typography variant="body1" fontWeight={700} sx={{ mb: 1, letterSpacing: '0.005em', color: T.heading }}>{t('markers_in_focus')}</Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mb: 1.5 }}>
                  {focusLabels.map((label, i) => (
                    <Chip key={i} label={label} size="small" variant="outlined" sx={{ fontSize: '0.75rem', height: 24 }} />
                  ))}
                </Box>
                <Typography variant="subtitle1" sx={{ lineHeight: 1.5, color: T.heading }}>{t('markers_analysis_scope')}</Typography>
              </Box>
              <Divider />
              <Box>
                <Typography variant="body1" fontWeight={700} sx={{ mb: 2, letterSpacing: '0.005em', color: T.heading }}>{t('score_what_title')}</Typography>
                <Box sx={{
                  p: 2.5, borderRadius: 2, border: '1px solid', borderColor: 'divider',
                  bgcolor: T.dark ? 'greydark.C900' : 'grey.C50',
                }}>
                  <Box sx={{ display: 'flex', gap: '6px', mb: 2.5 }}>
                    {Array.from({ length: 10 }).map((_, i) => (
                      <Box key={i} sx={{ width: 10, height: 10, borderRadius: '50%', backgroundColor: i < score ? cfg.dot : inactiveColor }} />
                    ))}
                  </Box>
                  <Typography variant="subtitle1" sx={{ lineHeight: 1.5, color: T.heading }}>{t('score_scale_desc')}</Typography>
                </Box>
              </Box>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {[
                  { count: 2, level: 'low',  descKey: 'score_low_desc'  },
                  { count: 5, level: 'med',  descKey: 'score_med_desc'  },
                  { count: 8, level: 'high', descKey: 'score_high_desc' },
                ].map(({ count, level, descKey }) => (
                  <Box key={level} sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, alignItems: { xs: 'flex-start', sm: 'center' }, gap: { xs: 1.5, sm: 2 } }}>
                    <Box sx={{ display: 'flex', gap: '6px', flexShrink: 0 }}>
                      {Array.from({ length: 10 }).map((_, i) => (
                        <Box key={i} sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: i < count ? RISK_CONFIG[level].dot : inactiveColor }} />
                      ))}
                    </Box>
                    <Typography variant="subtitle1" sx={{ lineHeight: 1.25, color: T.heading }}>{t(descKey)}</Typography>
                  </Box>
                ))}
              </Box>
            </Box>
          }
        />
      )}
      </Box>
    </Box>
  );
}

// ---------------------------------------------------------------------------
// PillarGroup — one area's card: heading (name + info) plus its numbered
// mechanism rows, used inside PillarMechanismsCard
// ---------------------------------------------------------------------------
function PillarGroup({ pillar, index, startNumber, markersMap, visitResults }) {
  const { T } = useTokens();
  const { t } = useTranslation();
  const [infoOpen, setInfoOpen] = useState(false);

  const meta = PILLAR_META[pillar.subsystem] ?? {};
  const label = meta.labelKey ? t(meta.labelKey) : pillar.subsystem;

  const infoContent = meta.infoKey ? (
    <Typography sx={{ fontSize: '0.85rem', lineHeight: 1.75 }}>
      {t(meta.infoKey)}
    </Typography>
  ) : null;

  const rows = pillar.mechanism_scores ?? [];

  return (
    <Box component="section" aria-label={label} sx={{
      ...glassTileSx(T, index),
      px: { xs: 0.5, sm: 1 }, pt: { xs: 1.75, sm: 2 }, pb: { xs: 0.75, sm: 1 },
      borderRadius: '24px',
    }}>
      {/* Area heading — lines up with the row content below */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, px: { xs: 1.5, sm: 2 }, mb: 0.5 }}>
        <Typography component="h3" sx={{ fontSize: { xs: '1.05rem', sm: '1.15rem' }, fontWeight: 700, color: T.heading, lineHeight: 1.3, letterSpacing: '-0.005em' }}>
          {label}
        </Typography>
        {infoContent && (
          <Tooltip title="Info" placement="top">
            <IconButton size="small" aria-label="Info" onClick={() => setInfoOpen(true)} sx={{ color: T.overline }}>
              <Info size={17} strokeWidth={2.25} />
            </IconButton>
          </Tooltip>
        )}
      </Box>

      {rows.map((ms, i) => (
        <Box key={ms.id ?? ms.mechanism}>
          {i > 0 && <Divider sx={{ mx: { xs: 1.5, sm: 2 }, borderColor: alpha(T.heading, T.dark ? 0.1 : 0.07) }} />}
          <MobileMechanismRow
            number={startNumber + i}
            name={ms.mechanism}
            title={ms.title}
            shortDescription={ms.short_description}
            markers={ms.markers}
            markersAttention={ms.markers_attention}
            score={ms.risk_score}
            description={ms.interpretation}
            markersMap={markersMap}
            visitResults={visitResults}
            {...(startNumber + i === 1 ? { 'data-tour': 'punkte-row' } : {})}
          />
        </Box>
      ))}

      {infoContent && (
        <CustomDialogSmall
          open={infoOpen}
          onClose={() => setInfoOpen(false)}
          title={label}
          content={infoContent}
        />
      )}
    </Box>
  );
}

// ---------------------------------------------------------------------------
// PillarMechanismsCard — one glass card holding all 4 pillars stacked
// top-down, each with its 3 mechanism rows numbered 1–12 continuously
// ---------------------------------------------------------------------------
function PillarMechanismsCard({ pillars, markersMap, visitResults }) {
  if (!pillars.length) return null;

  let cumulative = 0;

  return (
    <Box data-tour="saeulen-cards" sx={{ width: '100%', display: 'flex', flexDirection: 'column', gap: { xs: 1.5, sm: 2 } }}>
      {pillars.map((pillar, idx) => {
        const startNumber = cumulative + 1;
        cumulative += pillar.mechanism_scores?.length ?? 0;
        return (
          <PillarGroup
            key={pillar.subsystem}
            pillar={pillar}
            index={idx}
            startNumber={startNumber}
            markersMap={markersMap}
            visitResults={visitResults}
          />
        );
      })}
    </Box>
  );
}

// ---------------------------------------------------------------------------
// MechanismsHeader — page heading for the 12 mechanisms plus a summary that
// doubles as the legend: how many mechanisms sit in each risk band
// ---------------------------------------------------------------------------
const RISK_RANGES = { low: '0–3', med: '4–6', high: '7–10' };

// Page heading with its info button + dialog — the mechanisms and the
// recommendations page start with it
function SectionHeading({ title, info }) {
  const { T } = useTokens();
  const [open, setOpen] = useState(false);
  return (
    <>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <Typography component="h2" sx={{
          fontSize: { xs: '1.35rem', sm: '1.6rem' }, fontWeight: 700, color: T.heading,
          lineHeight: 1.2, letterSpacing: '-0.015em',
        }}>
          {title}
        </Typography>
        {info && (
          <Tooltip title="Info" placement="top">
            <IconButton aria-label="Info" onClick={() => setOpen(true)} sx={{ color: T.overline }}>
              <Info size={20} strokeWidth={2.25} />
            </IconButton>
          </Tooltip>
        )}
      </Box>
      {info && (
        <CustomDialogSmall
          open={open}
          onClose={() => setOpen(false)}
          title={title}
          content={
            <Typography sx={{ fontSize: '0.85rem', color: T.body, lineHeight: 1.75 }}>
              {info}
            </Typography>
          }
        />
      )}
    </>
  );
}

function MechanismsHeader({ pillars }) {
  const { T, RISK_CONFIG } = useTokens();
  const { t } = useTranslation();

  const counts = { low: 0, med: 0, high: 0 };
  pillars.flatMap((pl) => pl.mechanism_scores ?? [])
    .forEach((ms) => { counts[getRiskLevel(Number(ms.risk_score) || 0)] += 1; });

  return (
    <Box sx={{ mb: { xs: 2, sm: 2.5 }, px: { xs: 0.5, sm: 1 } }}>
      <SectionHeading title={t('hc_section_01_title')} info={t('hc_section_01_info')} />

      <Box sx={{ mt: 1.25, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
        {['high', 'med', 'low'].map((level) => {
          const cfg = RISK_CONFIG[level];
          return (
            <Box key={level} sx={{
              display: 'inline-flex', alignItems: 'center', gap: 0.875,
              pl: 1.25, pr: 1.5, py: 0.625, borderRadius: CHIP_RADIUS,
              bgcolor: cfg.bg, border: `1px solid ${cfg.border}`,
            }}>
              <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: cfg.dot, flexShrink: 0 }} />
              <Typography sx={{ fontSize: '0.84rem', color: T.heading, lineHeight: 1.3, whiteSpace: 'nowrap' }}>
                <Box component="span" sx={{ fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>{counts[level]}</Box>
                {' '}{t({ low: 'hc_level_low', med: 'hc_level_med', high: 'hc_level_high' }[level])}
                <Box component="span" sx={{ color: T.body, fontVariantNumeric: 'tabular-nums' }}> · {RISK_RANGES[level]}</Box>
              </Typography>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
}

// ---------------------------------------------------------------------------
// Glass tile surface shared by the BioAge tile and the biometric tiles
// ---------------------------------------------------------------------------
const EASE_OUT = 'cubic-bezier(0.23, 1, 0.32, 1)';

// One chip shape for every pill on the cockpit (status, score, out-of-range):
// same radius, same height
const CHIP_RADIUS = '12px';
const CHIP_SX = { px: 1.25, py: 0.25, borderRadius: CHIP_RADIUS };
const CHIP_TEXT_SX = { fontSize: '0.75rem', fontWeight: 700, lineHeight: 1.5, fontVariantNumeric: 'tabular-nums' };

// Text link with an up-right arrow, used in card corners
const linkButtonSx = (T) => ({
  gap: 0.5, pl: 1, pr: 0.5, py: 0.5, borderRadius: '10px',
  fontSize: '0.85rem', fontWeight: 500, color: T.heading, whiteSpace: 'nowrap',
  transition: `background-color 150ms ease, transform 160ms ${EASE_OUT}`,
  '& svg': { color: T.arrow, transition: `transform 200ms ${EASE_OUT}` },
  '@media (hover: hover) and (pointer: fine)': {
    '&:hover': { bgcolor: alpha(T.heading, T.dark ? 0.06 : 0.045) },
    '&:hover svg': { transform: 'translate(2px, -2px)' },
  },
  '&:active': { transform: 'scale(0.97)' },
  '&.Mui-focusVisible': { outline: `2px solid ${T.arrow}`, outlineOffset: 2 },
});

const glassTileSx = (T, i = 0) => ({
  display: 'flex', flexDirection: 'column', minWidth: 0,
  borderRadius: '28px',
  bgcolor: T.dark ? alpha('#fff', 0.04) : alpha('#fff', 0.5),
  backdropFilter: 'blur(28px) saturate(180%)',
  WebkitBackdropFilter: 'blur(28px) saturate(180%)',
  border: `1px solid ${T.dark ? 'rgba(255,255,255,0.08)' : 'rgba(255,255,255,0.65)'}`,
  boxShadow: T.dark
    ? 'inset 0 1px 0 rgba(255,255,255,0.06), 0 8px 32px rgba(0,0,0,0.55)'
    : 'inset 0 1px 0 rgba(255,255,255,0.7), 0 4px 24px rgba(0,0,0,0.06)',
  px: { xs: 2, sm: 2.5 }, pt: { xs: 2.25, sm: 2.75 }, pb: { xs: 2, sm: 2.5 },
  animation: `hc-fade-up 0.5s ${EASE_OUT} ${0.06 + i * 0.05}s both`,
  '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
});

// Column grid shared by the BioAge row and the biometrics row
const TILE_GRID_SX = {
  display: 'grid',
  gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(4, 1fr)' },
  gap: { xs: 1.5, sm: 2 },
};

// Dark (greydark) tile surface — BioAge tile and the biometric tiles
const darkTileSx = (T, theme, i = 0) => ({
  ...glassTileSx(T, i),
  bgcolor: theme.palette.greydark.C900,
  backdropFilter: 'none', WebkitBackdropFilter: 'none',
  border: `1px solid ${T.dark ? 'rgba(255,255,255,0.1)' : alpha(theme.palette.greydark.C900, 0.9)}`,
  boxShadow: T.dark
    ? 'inset 0 1px 0 rgba(255,255,255,0.06), 0 8px 32px rgba(0,0,0,0.55)'
    : 'inset 0 1px 0 rgba(255,255,255,0.08), 0 6px 24px rgba(0,0,0,0.18)',
});

// Chip colours for a dark tile: tint of the status colour
const darkChip = (color, label) => ({ color, bg: alpha(color, 0.16), border: alpha(color, 0.45), label });

const tileLabelSx = (T) => ({
  fontSize: { xs: '0.68rem', sm: '0.75rem' }, fontWeight: 700, color: T.body,
  textTransform: 'uppercase', letterSpacing: '0.08em', lineHeight: 1.3, minHeight: '2.6em',
});

const tileValueSx = {
  fontFamily: "'Poppins', sans-serif", fontSize: { xs: '1.6rem', sm: '2rem' }, fontWeight: 600,
  lineHeight: 1.1, letterSpacing: '-0.02em', fontVariantNumeric: 'tabular-nums',
};

function TilePill({ s }) {
  return (
    <Box sx={{ mt: 'auto', pt: 1.5, alignSelf: 'flex-start' }}>
      <Box sx={{ ...CHIP_SX, bgcolor: s.bg, border: `1px solid ${s.border}` }}>
        <Typography sx={{ ...CHIP_TEXT_SX, color: s.color }}>
          {s.label}
        </Typography>
      </Box>
    </Box>
  );
}

// ---------------------------------------------------------------------------
// BioAgeCard — tile in the biometrics row: biological vs. calendar age of the
// latest measurement up to the selected visit, arrow to the BioAge page
// ---------------------------------------------------------------------------
function BioAgeCard({ selectedDate }) {
  const { T } = useTokens();
  const theme = useTheme();
  const { t, language } = useTranslation();
  const { state } = useAuth();
  const navigate = useNavigate();
  const [entries, setEntries] = useState([]);
  // null until known; false only when the API confirms the add-on was not bought
  const [purchased, setPurchased] = useState(null);

  useEffect(() => {
    let cancelled = false;
    axios.get(`${API_URL}/phenoages/`)
      .then(({ data }) => { if (!cancelled && Array.isArray(data)) setEntries(data); })
      .catch(() => {});
    getAddonPurchased('age')
      .then((res) => { if (!cancelled) setPurchased(!!res?.purchased); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  const label = (onDark) => (
    <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 0.5 }}>
      <Typography component="h3" sx={{ ...tileLabelSx(T), ...(onDark && { color: alpha('#fff', 0.62) }) }}>
        {t('nav_biological_age')}
      </Typography>
      <Tooltip title={t('hc_explore_details')} placement="top">
        <IconButton size="small" aria-label={t('hc_explore_details')} onClick={() => navigate('/BioAgeWelcome')}
          sx={{ color: onDark ? alpha('#fff', 0.75) : T.arrow, p: 0.5, mt: -0.75, mr: -0.75, flexShrink: 0 }}>
          <ArrowUpRight size={20} strokeWidth={3} />
        </IconButton>
      </Tooltip>
    </Box>
  );

  // Add-on not ordered and no measurements at all → "not selected" tile
  if (!entries.length && purchased === false) {
    return (
        <Box sx={{
          ...glassTileSx(T, 0),
          // Empty state: dashed border, no glass shadow
          boxShadow: 'none',
          border: `1px dashed ${T.dark ? 'rgba(255,255,255,0.16)' : alpha(T.heading, 0.25)}`,
          bgcolor: T.dark ? alpha('#fff', 0.02) : alpha('#fff', 0.35),
        }}>
          {label(false)}
          <Typography sx={{ ...tileValueSx, mt: 1, color: T.body }}>—</Typography>
          <Typography sx={{ mt: 0.75, fontSize: { xs: '0.75rem', sm: '0.8rem' }, color: T.body, lineHeight: 1.45 }}>
            {t('hc_bioage_not_chosen')}
          </Typography>
        </Box>
    );
  }

  // Latest measurement on or before the selected lab visit
  const entry = entries
    .filter((e) => !isNaN(parseFloat(e.bioage)) && String(e.date).slice(0, 10) <= selectedDate)
    .sort((a, b) => new Date(b.date) - new Date(a.date))[0];
  if (!entry) return null;

  const bioAge = parseFloat(entry.bioage);
  // Calendar age at the time of the measurement; falls back to the profile age
  const reportedDiff = parseFloat(entry.agediff);
  const calendarAge = !isNaN(reportedDiff) ? bioAge - reportedDiff : parseFloat(state.user?.age);
  const ageDiff = !isNaN(reportedDiff) ? reportedDiff : bioAge - calendarAge;

  const locale = language === 'de' ? 'de-DE' : 'en-GB';
  const fmt = (n, signed = false) => new Intl.NumberFormat(locale, {
    minimumFractionDigits: 1, maximumFractionDigits: 1,
    signDisplay: signed ? 'exceptZero' : 'auto',
  }).format(n).replace('-', '−');

  // Younger → green, older → red, same age → white
  const tone = isNaN(ageDiff) || Math.abs(ageDiff) <= 0.05 ? '#fff' : ageDiff < 0 ? T.green : T.red;
  const chip = !isNaN(ageDiff) && darkChip(tone, `${fmt(ageDiff, true)} ${t('tornado_dialog_years')}`);

  return (
      <>
      {/* Dark (greydark) surface sets this tile apart from the glass tiles */}
      <Box sx={darkTileSx(T, theme, 0)}>
        {label(true)}

        <Box sx={{ mt: 1, display: 'flex', alignItems: 'baseline', gap: 0.5, flexWrap: 'wrap' }}>
          <Typography sx={{ ...tileValueSx, color: tone }}>{fmt(bioAge)}</Typography>
          <Typography sx={{ fontSize: { xs: '0.75rem', sm: '0.85rem' }, color: alpha('#fff', 0.7) }}>{t('tornado_dialog_years')}</Typography>
        </Box>

        {!isNaN(calendarAge) && (
          <Typography sx={{ mt: 0.75, fontSize: { xs: '0.75rem', sm: '0.8rem' }, color: alpha('#fff', 0.7), lineHeight: 1.45 }}>
            {t('hc_bioage_calendar')} {Math.round(calendarAge)} {t('tornado_dialog_years')}
          </Typography>
        )}

        {chip && <TilePill s={chip} />}
      </Box>
      </>
  );
}

// ---------------------------------------------------------------------------
// ExecSummary — "Das Wichtigste in Kürze": one tinted card per bullet, coloured
// by its status. Status comes from the item's `status` field when the report
// sends it, else from the mechanism with the same title, else neutral.
// ---------------------------------------------------------------------------
const SUMMARY_STATUS = {
  stable:    { tone: 'green' },
  attention: { tone: 'amber' },
  support:   { tone: 'amber' },
  elevated:  { tone: 'red' },
};
const STATUS_BY_RISK = { low: 'stable', med: 'attention', high: 'elevated' };

function ExecSummary({ items, pillars }) {
  const { T } = useTokens();
  const theme = useTheme();
  const { t } = useTranslation();

  const norm = (v) => String(v ?? '').trim().toLowerCase();
  const mechanisms = pillars.flatMap((pl) => pl.mechanism_scores ?? []).map((ms) => {
    const titleKey = mechLookup(MECHANISM_TITLE_KEYS, ms.mechanism);
    return { title: norm(ms.title ?? (titleKey ? t(titleKey) : ms.mechanism)), score: Number(ms.risk_score) || 0 };
  });
  const statusOf = (item) => {
    const own = norm(item.status);
    if (SUMMARY_STATUS[own]) return own;
    const want = norm(item.title);
    const ms = mechanisms.find((m) => m.title === want)
      ?? mechanisms.find((m) => want && m.title && (m.title.includes(want) || want.includes(m.title)));
    return ms ? STATUS_BY_RISK[getRiskLevel(ms.score)] : null;
  };

  return (
    <Box sx={{ mt: 5 }}>
      <Box sx={{ display: 'flex', alignItems: 'baseline', flexWrap: 'wrap', columnGap: 2, rowGap: 0.5, mb: 2.5 }}>
        <Typography sx={{ fontSize: '0.75rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.12em', color: T.accentText }}>
          {t('hc_exec_summary_eyebrow')}
        </Typography>
        <Typography component="h2" sx={{ fontSize: { xs: '1.3rem', sm: '1.55rem' }, fontWeight: 700, color: T.heading, letterSpacing: '-0.01em', lineHeight: 1.2 }}>
          {t('hc_exec_summary_heading')}
        </Typography>
      </Box>

      {items.length > 0 ? (
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))' }, gap: { xs: 1.5, sm: 2 } }}>
          {items.map((item, i) => {
            const status = statusOf(item);
            const cfg = status && SUMMARY_STATUS[status];
            const color = cfg ? T[cfg.tone] : (T.dark ? theme.palette.greydark.C400 : theme.palette.grey.C400);
            return (
              <Box key={i} sx={{
                borderRadius: '16px',
                borderLeft: `6px solid ${color}`,
                bgcolor: alpha(color, T.dark ? 0.12 : cfg ? 0.1 : 0.08),
                px: { xs: 2.25, sm: 3 }, py: { xs: 2, sm: 2.5 },
                animation: `hc-fade-up 0.5s ${EASE_OUT} ${i * 60}ms both`,
                '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
              }}>
                <Typography component="h3" sx={{ mb: 1, fontSize: { xs: '1.05rem', sm: '1.15rem' }, fontWeight: 700, color: T.heading, lineHeight: 1.3 }}>
                  {item.title}
                </Typography>
                <Typography sx={{ fontSize: { xs: '0.92rem', sm: '0.98rem' }, color: T.bodyStrong, lineHeight: 1.65 }}>
                  {item.text}
                </Typography>
              </Box>
            );
          })}
        </Box>
      ) : <NewFeaturePending />}
    </Box>
  );
}

// Shown in place of a field that only new-format reports carry
function NewFeaturePending() {
  const { T } = useTokens();
  const { t } = useTranslation();
  return (
    <Typography sx={{ fontSize: '0.85rem', color: T.body, lineHeight: 1.6, fontStyle: 'italic' }}>
      {t('hc_new_feature_pending')}
    </Typography>
  );
}

// ---------------------------------------------------------------------------
// SectionLabel
// ---------------------------------------------------------------------------
function SectionLabel({ index, title, info }) {
  const { T } = useTokens();
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);

  return (
    <>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2.5, mb: 2.5 }}>

        {/* Title block */}
        <Box sx={{ flexShrink: 0 }}>
          <Typography sx={{
            fontSize: '0.55rem', fontWeight: 800, letterSpacing: '0.18em',
            textTransform: 'uppercase', color: T.overline,
          }}>
            {t('hc_section_label')} {index}
          </Typography>
          <Typography sx={{
            fontSize: '1.05rem', fontWeight: 700, color: T.heading,
            mt: 0.15, lineHeight: 1.2,
          }}>
            {title}
          </Typography>
        </Box>

        <Box sx={{ flex: 1 }} />

        {/* Info button */}
        {info && (
          <IconButton
            onClick={() => setOpen(true)}
            sx={{ color: T.overline, '&:hover': { color: T.body }, p: 0.5, flexShrink: 0 }}
          >
            <Info size={22} strokeWidth={2.5} />
          </IconButton>
        )}
      </Box>

      {info && (
        <CustomDialogSmall
          open={open}
          onClose={() => setOpen(false)}
          title={title}
          content={
            typeof info === 'string' ? (
              <Typography sx={{ fontSize: '0.85rem', color: T.body, lineHeight: 1.75 }}>
                {info}
              </Typography>
            ) : info
          }
        />
      )}
    </>
  );
}

// ---------------------------------------------------------------------------
// Skeleton
// ---------------------------------------------------------------------------
function CockpitSkeleton() {
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', xl: 'repeat(4,1fr)' }, gap: 2.5 }}>
        {[1,2,3,4].map(n => <Skeleton key={n} variant="rounded" height={300} sx={{ borderRadius: '18px' }} />)}
      </Box>
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(3,1fr)' }, gap: 2.5 }}>
        {[1,2,3].map(n => <Skeleton key={n} variant="rounded" height={220} sx={{ borderRadius: '18px' }} />)}
      </Box>
      <Skeleton variant="rounded" height={200} sx={{ borderRadius: '18px' }} />
    </Box>
  );
}

// ---------------------------------------------------------------------------
// Part 2 — Recommendation card
// ---------------------------------------------------------------------------
function RecommendationCard({ rec, index, animIndex = 0, mechanisms = [] }) {
  const { T } = useTokens();
  const theme = useTheme();
  const { t } = useTranslation();
  const [open, setOpen] = useState(true);
  const accent = theme.palette.primary.main;
  // Recommendation number in greydark (light text colour in dark mode, where greydark would vanish)
  const numberColor = T.dark ? T.heading : theme.palette.greydark.C900;

  const fieldTitleSx = { fontSize: '0.88rem', fontWeight: 700, color: T.heading, lineHeight: 1.3, mb: 0.5 };
  const fieldDividerSx = { mb: 1, borderColor: alpha(T.heading, T.dark ? 0.14 : 0.1) };
  const copySx = { fontSize: { xs: '0.92rem', sm: '0.95rem' }, color: T.bodyStrong, lineHeight: 1.7, m: 0, maxWidth: '70ch' };
  const steps = rec.how_steps?.length > 0 ? rec.how_steps : null;

  return (
    <Paper
      elevation={0}
      {...(index === 0 ? { 'data-tour': 'empfehlung-top' } : {})}
      sx={{
        borderRadius: '24px',
        bgcolor: T.dark ? alpha('#fff', 0.04) : alpha('#fff', 0.5),
        backdropFilter: 'blur(28px) saturate(180%)',
        WebkitBackdropFilter: 'blur(28px) saturate(180%)',
        border: `1px solid ${T.dark ? 'rgba(255,255,255,0.08)' : 'rgba(255,255,255,0.65)'}`,
        boxShadow: T.dark
          ? 'inset 0 1px 0 rgba(255,255,255,0.06), 0 8px 32px rgba(0,0,0,0.55)'
          : 'inset 0 1px 0 rgba(255,255,255,0.7), 0 4px 24px rgba(0,0,0,0.06)',
        overflow: 'hidden',
        animation: `hc-fade-up 0.5s ${EASE_OUT} ${animIndex * 70}ms both`,
        '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
      }}
    >
      {/* Header — toggles the details */}
      <ButtonBase
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        sx={{
          width: '100%', textAlign: 'left', alignItems: 'flex-start', justifyContent: 'flex-start',
          gap: { xs: 1.5, sm: 2 }, px: { xs: 2.25, sm: 3 }, pt: { xs: 2.25, sm: 2.75 }, pb: open ? 1.5 : { xs: 2.25, sm: 2.75 },
          transition: 'background-color 150ms ease',
          '@media (hover: hover) and (pointer: fine)': { '&:hover': { bgcolor: alpha(T.heading, T.dark ? 0.04 : 0.025) } },
          '&.Mui-focusVisible': { boxShadow: `inset 0 0 0 2px ${theme.palette.tertiary.C300}` },
        }}
      >
        {/* Step badge (design-system pattern) */}
        <Box sx={{
          flexShrink: 0, width: 30, height: 30, mt: '-2px', borderRadius: '50%',
          border: `2px solid ${numberColor}`,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <Typography variant="number4" sx={{ color: numberColor, fontWeight: 700, fontSize: '0.8rem', lineHeight: 1 }}>
            {index + 1}
          </Typography>
        </Box>

        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography component="h3" sx={{ fontSize: { xs: '1.05rem', sm: '1.15rem' }, fontWeight: 700, color: T.heading, lineHeight: 1.35, letterSpacing: '-0.005em' }}>
            {rec.what}
          </Typography>
        </Box>

        <Box sx={{
          display: 'flex', flexShrink: 0, mt: '3px', color: T.arrow,
          transform: open ? 'rotate(180deg)' : 'none',
          transition: `transform 200ms ${EASE_OUT}`,
        }}>
          <ChevronDown size={22} strokeWidth={2.5} />
        </Box>
      </ButtonBase>

      <Collapse in={open} timeout={220}>
        <Box sx={{
          px: { xs: 2.25, sm: 3 }, pb: { xs: 2.25, sm: 3 }, pt: 0.5,
          // Body lines up with the title, not with the step badge
          pl: { xs: 2.25, sm: 'calc(24px + 30px + 16px)' },
          display: 'flex', flexDirection: 'column', gap: 2.25,
        }}>
          {/* Why + what it brings — side by side on wide screens */}
          <Box sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: rec.impact ? 'repeat(2, minmax(0, 1fr))' : '1fr' },
            columnGap: 4, rowGap: 2,
          }}>
            <Box>
              <Typography sx={fieldTitleSx}>{t('hc_rec_background')}</Typography>
              <Divider sx={fieldDividerSx} />
              <Box component="p" sx={copySx}>{rec.why}</Box>
            </Box>
            {rec.impact && (
              <Box>
                <Typography sx={fieldTitleSx}>{t('hc_rec_impact')}</Typography>
                <Divider sx={fieldDividerSx} />
                <Box component="p" sx={copySx}>{rec.impact}</Box>
              </Box>
            )}
          </Box>

          {/* Goal — the user's actual target */}
          {(rec.goal || rec.target_marker) && (
            <Box sx={{
              display: 'flex', gap: 1.5, alignItems: 'flex-start',
              p: 2, borderRadius: '16px',
              bgcolor: alpha(T.heading, T.dark ? 0.05 : 0.035),
            }}>
              <Box sx={{ display: 'flex', color: accent, mt: '2px', flexShrink: 0 }}>
                <Target size={20} strokeWidth={2.25} />
              </Box>
              <Box sx={{ minWidth: 0 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1, mb: 0.25 }}>
                  <Typography sx={{ ...fieldTitleSx, mb: 0 }}>{t('hc_rec_goal')}</Typography>
                  {rec.target_marker && (
                    <Chip label={rec.target_marker} size="small" variant="outlined" sx={{ height: 22, fontSize: '0.72rem', fontWeight: 600 }} />
                  )}
                </Box>
                {rec.goal && (
                  <Typography sx={{ fontSize: { xs: '0.95rem', sm: '1rem' }, fontWeight: 600, color: T.heading, lineHeight: 1.55 }}>
                    {rec.goal}
                  </Typography>
                )}
              </Box>
            </Box>
          )}

          {/* How to act — numbered steps */}
          <Box sx={{ p: { xs: 2, sm: 2.25 }, borderRadius: '16px', bgcolor: alpha(accent, T.dark ? 0.14 : 0.07) }}>
            <Typography sx={{ ...fieldTitleSx, color: accent, mb: steps ? 0.25 : 0.75 }}>{t('hc_how_to_act')}</Typography>
            {steps ? (
              <Box component="ol" sx={{ listStyle: 'none', m: 0, p: 0 }}>
                {steps.map((step, i) => (
                  <Box component="li" key={i}>
                    {i > 0 && <Divider sx={{ borderColor: alpha(accent, T.dark ? 0.25 : 0.18) }} />}
                    <Stack direction="row" spacing={1.5} alignItems="flex-start" sx={{ py: 1.25 }}>
                      <Box sx={{
                        flexShrink: 0, width: 24, height: 24, mt: '1px', borderRadius: '50%',
                        border: `2px solid ${accent}`,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                      }}>
                        <Typography variant="number4" sx={{ color: accent, fontWeight: 700, fontSize: '0.7rem', lineHeight: 1 }}>
                          {i + 1}
                        </Typography>
                      </Box>
                      <Typography sx={{ ...copySx, color: T.heading }}>{step}</Typography>
                    </Stack>
                  </Box>
                ))}
              </Box>
            ) : (
              <Typography sx={{ ...copySx, color: T.heading, whiteSpace: 'pre-line' }}>{rec.how}</Typography>
            )}
          </Box>

          {/* Mechanisms this recommendation works on */}
          {rec.addresses?.length > 0 && (
            <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 0.75 }}>
              <Typography sx={{ fontSize: '0.8rem', color: T.body, mr: 0.25 }}>{t('hc_rec_addresses')}</Typography>
              {rec.addresses.map((a) => (
                <Chip
                  key={a}
                  // New-format reports send mechanism ids here
                  label={mechanisms.find((m) => m.id === a)?.title ?? a}
                  size="small"
                  sx={{
                    height: 24, fontSize: '0.75rem', fontWeight: 600,
                    bgcolor: alpha(accent, T.dark ? 0.15 : 0.08), color: accent,
                    border: `1px solid ${alpha(accent, T.dark ? 0.4 : 0.25)}`,
                  }}
                />
              ))}
            </Box>
          )}
        </Box>
      </Collapse>
    </Paper>
  );
}

// ---------------------------------------------------------------------------
// Part 3 — Doctor note card
// ---------------------------------------------------------------------------
function DoctorNoteCard({ note, index, animIndex = 0 }) {
  const { T } = useTokens();
  const theme = useTheme();
  const { t } = useTranslation();
  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: '1fr',
        gap: 2.5,
        py: 3.5,
        borderBottom: `1px solid ${T.cardBorder}`,
        '&:first-of-type': { pt: 0 },
        '&:last-child': { borderBottom: 'none', pb: 0 },
        animation: 'hc-fade-up 0.5s ease both',
        animationDelay: `${animIndex * 80}ms`,
      }}
    >
      {/* Topic + markers */}
      <Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, mb: 1.5 }}>
          <Box sx={{
            width: 28, height: 28, borderRadius: '50%', flexShrink: 0,
            bgcolor: 'transparent', border: `1px solid ${theme.palette.primary.main}`,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <Typography sx={{
              fontSize: '0.7rem', fontWeight: 800, color: theme.palette.primary.main,
            }}>
              {index + 1}
            </Typography>
          </Box>
          <Typography sx={{
            fontSize: '0.88rem', fontWeight: 700, color: T.heading, lineHeight: 1.35,
          }}>
            {note.topic}
          </Typography>
        </Box>
        {note.markers_involved?.length > 0 && (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
            {note.markers_involved.map((m) => (
              <Chip
                key={m}
                label={m}
                size="small"
                sx={{
                  height: '20px',
                  fontSize: '0.58rem',
                  fontWeight: 700,
                  letterSpacing: '0.06em',
                  textTransform: 'uppercase',
                  bgcolor: T.chipBg,
                  color: T.chipText,
                  borderRadius: '999px',
                }}
              />
            ))}
          </Box>
        )}
      </Box>

      {/* Context + question */}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.75 }}>
        <Typography sx={{ fontSize: '0.82rem', color: T.body, lineHeight: 1.75 }}>
          {note.context_evaluation}
        </Typography>
        <Box sx={{
          p: 2.25, borderRadius: '12px',
          bgcolor: alpha(theme.palette.primary.main, T.dark ? 0.15 : 0.08),
          border: `1px solid ${alpha(theme.palette.primary.main, T.dark ? 0.4 : 0.3)}`,
        }}>
          <Typography sx={{
            fontSize: '0.58rem', fontWeight: 800, color: theme.palette.primary.main,
            letterSpacing: '0.05em', textTransform: 'uppercase', mb: 0.75,
          }}>
            {t('hc_ask_doctor')}
          </Typography>
          <Box component="ul" sx={{
            m: 0, pl: 2.5,
            fontSize: '0.85rem', color: T.bodyStrong, lineHeight: 1.75, fontStyle: 'italic',
            '& li + li': { mt: 1.25 },
          }}>
            {note.doctor_question && <li>"{note.doctor_question}"</li>}
            {note.sub_questions?.map((question, i) => (
              <li key={i}>"{question}"</li>
            ))}
          </Box>
        </Box>
      </Box>
    </Box>
  );
}

// ---------------------------------------------------------------------------
// BiometricsRow — four small tiles below the BioAge card: BMI, waist-to-height
// ratio, waist and blood pressure (latest values from the Vitals data)
// ---------------------------------------------------------------------------
// Same thresholds as the Vitals page (GetBmi.jsx / BpPanel.jsx) — keep in sync
const evalBmi   = (v) => (v == null ? null : v < 18.5 ? 'LOW' : v < 25 ? 'NORMAL' : 'HIGH');
const evalWhtr  = (v) => (v == null ? null : v < 0.5 ? 'NORMAL' : 'HIGH');
const evalWaist = (v, gender) => (v == null ? null : v < (gender === 'F' ? 88 : 102) ? 'NORMAL' : 'HIGH');
const evalBp = (sys, dia) => {
  if (sys == null || dia == null) return null;
  if (sys < 121 && dia < 81) return 'NORMAL';
  if (sys < 131 && dia < 89) return 'ELEVATED';
  return 'HIGH';
};

// 7-day average like the Vitals page; falls back to the latest reading
function currentBp(records) {
  if (!records.length) return null;
  const sorted = [...records].sort((a, b) => new Date(b.m_date) - new Date(a.m_date));
  const cutoff = new Date();
  cutoff.setDate(cutoff.getDate() - 7);
  const recent = sorted.filter((r) => new Date(`${r.m_date}T00:00:00`) >= cutoff);
  if (!recent.length) return { sys: sorted[0].systole_mmHg, dia: sorted[0].diastole_mmHg };
  const avg = (key) => Math.round(recent.reduce((s, r) => s + Number(r[key]), 0) / recent.length);
  return { sys: avg('systole_mmHg'), dia: avg('diastole_mmHg') };
}

function BiometricsRow() {
  const { T } = useTokens();
  const theme = useTheme();
  const navigate = useNavigate();
  // Expanded tiles by key; all collapsed by default
  const [open, setOpen] = useState({});
  const { t, language } = useTranslation();
  const { state } = useAuth();
  const gender = state.user?.gender;
  const [body, setBody] = useState([]);
  const [bp, setBp]     = useState([]);

  useEffect(() => {
    let cancelled = false;
    axios.get(WEIGHTDATA)
      .then(({ data }) => { if (!cancelled && Array.isArray(data)) setBody(data); })
      .catch(() => {});
    axios.get(BPLIST)
      .then(({ data }) => { if (!cancelled && Array.isArray(data)) setBp(data); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  // Latest record that actually carries the field (records are oldest first)
  const latest = (key) => {
    const rec = [...body].reverse().find((r) => r[key] != null && r[key] !== '');
    return rec ? parseFloat(rec[key]) : null;
  };
  const bmi = latest('bmi'), whtr = latest('whtr'), waist = latest('waist_cm');
  const bpNow = currentBp(bp);

  if (bmi == null && whtr == null && waist == null && !bpNow) return null;

  const locale = language === 'de' ? 'de-DE' : 'en-GB';
  const fmt = (n, digits) => new Intl.NumberFormat(locale, { minimumFractionDigits: digits, maximumFractionDigits: digits }).format(n);

  const tiles = [
    { key: 'bmi',   label: t('hc_bio_label_bmi'),          value: bmi != null ? fmt(bmi, 1) : null,     unit: 'kg/m²', ref: t('hc_bio_ref_bmi'),  status: evalBmi(bmi) },
    { key: 'whtr',  label: t('hc_bio_label_whtr'),         value: whtr != null ? fmt(whtr, 2) : null,   unit: '',      ref: t('hc_bio_ref_whtr'), status: evalWhtr(whtr) },
    { key: 'waist', label: t('hc_bio_label_waist'),        value: waist != null ? fmt(waist, 0) : null, unit: 'cm',
      ref: gender === 'F' ? t('hc_bio_ref_waist_f') : t('hc_bio_ref_waist_m'), status: evalWaist(waist, gender) },
    { key: 'bp',    label: t('hc_bio_label_bp'),           value: bpNow ? `${bpNow.sys}/${bpNow.dia}` : null, unit: 'mmHg', ref: t('hc_bio_ref_bp'), status: bpNow ? evalBp(bpNow.sys, bpNow.dia) : null },
  ];

  const STATUS = {
    HIGH:     { color: T.red,   bg: T.redBg,   border: T.redBorder,   label: t('hc_bio_high') },
    ELEVATED: { color: T.amber, bg: T.amberBg, border: T.amberBorder, label: t('hc_bio_elevated') },
    LOW:      { color: T.amber, bg: T.amberBg, border: T.amberBorder, label: t('hc_bio_low') },
    NORMAL:   { color: T.green, bg: T.greenBg, border: T.greenBorder, label: t('hc_bio_normal') },
  };

  const toggle = (key) => setOpen((o) => ({ ...o, [key]: !o[key] }));

  return (
    // alignItems start: an expanded tile doesn't stretch its collapsed neighbours
    <Box sx={{ ...TILE_GRID_SX, alignItems: 'start' }}>
      {tiles.map((tile, i) => {
        const s = STATUS[tile.status];
        const flagged = s && tile.status !== 'NORMAL';
        // Small traffic light under the arrow: green = normal, red = flagged, none = no data
        const light = s ? (flagged ? T.red : T.green) : null;
        const isOpen = !!open[tile.key];
        return (
          // The whole tile is the toggle. A <div> (ButtonBase keeps role="button",
          // focus and keyboard) — a native <button> would centre its content.
          // px/pt/pb (not p): the responsive px/pt/pb from darkTileSx compile to
          // media queries that would otherwise win over a plain `p: 0`
          <ButtonBase
            key={tile.key}
            component="div"
            onClick={() => toggle(tile.key)}
            aria-expanded={isOpen}
            aria-controls={`hc-bio-${tile.key}`}
            sx={{
              ...darkTileSx(T, theme, i + 1), px: 0, pt: 0, pb: 0, overflow: 'hidden',
              display: 'flex', flexDirection: 'column', alignItems: 'stretch', justifyContent: 'flex-start',
              textAlign: 'left', cursor: 'pointer',
              // Expanded tiles stretch to the tallest one in their row (same
              // height); collapsed tiles stay compact
              alignSelf: isOpen ? 'stretch' : 'start',
              transition: `filter 150ms ease, transform 160ms ${EASE_OUT}`,
              '@media (hover: hover) and (pointer: fine)': {
                '&:hover': { filter: 'brightness(1.15)' },
              },
              '&:active': { transform: 'scale(0.985)' },
              '&.Mui-focusVisible': { boxShadow: `0 0 0 2px ${alpha('#fff', 0.6)}` },
            }}
          >
            {/* Header: label + small traffic light (top right) */}
            <Box sx={{
              // Block layout so the light can float right: the first label line
              // wraps around it, the second uses the full width
              display: 'block',
              px: { xs: 2, sm: 2.5 }, pt: { xs: 2, sm: 2.25 }, pb: isOpen ? 0 : { xs: 2, sm: 2.25 },
              transition: 'padding 220ms ease-out',
            }}>
              {light && (
                <Box aria-hidden="true" sx={{
                  float: 'right', ml: 1, mt: '4px',
                  width: 8, height: 8, borderRadius: '50%', bgcolor: light,
                  boxShadow: `0 0 0 2px ${alpha(light, 0.2)}`,
                }} />
              )}
              {/* Label: always exactly two lines, left aligned → every tile has the same height */}
              <Typography component="h3" sx={{
                fontSize: { xs: '0.62rem', sm: '0.7rem' }, fontWeight: 700, color: alpha('#fff', 0.62),
                textTransform: 'uppercase', letterSpacing: '0.06em', lineHeight: 1.35, height: '2.7em',
              }}>
                {tile.label.split('\n').map((line, li) => (
                  <Box key={li} component="span" sx={{ display: 'block', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {line}
                  </Box>
                ))}
                {/* Status for screen readers (colour alone isn't announced) */}
                <Box component="span" sx={{ position: 'absolute', width: 1, height: 1, overflow: 'hidden', clip: 'rect(0 0 0 0)', whiteSpace: 'nowrap' }}>
                  {s ? `: ${s.label}` : `: ${t('hc_bio_no_data')}`}
                </Box>
              </Typography>
            </Box>

            {/* Details: value, reference, status */}
            <Collapse in={isOpen} timeout={220} easing={EASE_OUT} id={`hc-bio-${tile.key}`}
              sx={{
                flexGrow: isOpen ? 1 : 0,
                '& .MuiCollapse-wrapper, & .MuiCollapse-wrapperInner': { height: '100%' },
              }}>
              <Box sx={{ height: '100%', boxSizing: 'border-box', px: { xs: 2, sm: 2.5 }, pb: { xs: 2, sm: 2.5 }, display: 'flex', flexDirection: 'column' }}>
                <Box sx={{ mt: 0.5, display: 'flex', alignItems: 'baseline', gap: 0.5, flexWrap: 'wrap' }}>
                  <Typography sx={{
                    ...tileValueSx,
                    color: tile.value == null ? alpha('#fff', 0.5) : flagged ? s.color : '#fff',
                  }}>
                    {tile.value ?? '—'}
                  </Typography>
                  {tile.value != null && tile.unit && (
                    <Typography sx={{ fontSize: { xs: '0.75rem', sm: '0.85rem' }, color: alpha('#fff', 0.7) }}>{tile.unit}</Typography>
                  )}
                </Box>

                <Typography sx={{ mt: 0.75, fontSize: { xs: '0.75rem', sm: '0.8rem' }, color: alpha('#fff', 0.7), lineHeight: 1.45 }}>
                  {tile.value == null ? t('hc_bio_no_data') : tile.ref}
                </Typography>

                {/* Last row: status chip + arrow to the Vitals page */}
                <Box sx={{ mt: 'auto', pt: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
                  {s ? (() => {
                    const c = darkChip(s.color, s.label);
                    return (
                      <Box sx={{ ...CHIP_SX, bgcolor: c.bg, border: `1px solid ${c.border}` }}>
                        <Typography sx={{ ...CHIP_TEXT_SX, color: c.color }}>{c.label}</Typography>
                      </Box>
                    );
                  })() : <span />}
                  <Tooltip title={t('hc_bio_open_vitals')} placement="top">
                    <IconButton
                      size="small"
                      aria-label={t('hc_bio_open_vitals')}
                      // Don't toggle the tile when following the link
                      onClick={(e) => { e.stopPropagation(); navigate('/VitalsWelcome'); }}
                      onKeyDown={(e) => e.stopPropagation()}
                      onMouseDown={(e) => e.stopPropagation()}
                      sx={{ color: alpha('#fff', 0.75), p: 0.5, mr: -0.5, '&:hover': { bgcolor: alpha('#fff', 0.08) } }}
                    >
                      <ArrowUpRight size={20} strokeWidth={3} />
                    </IconButton>
                  </Tooltip>
                </Box>
              </Box>
            </Collapse>
          </ButtonBase>
        );
      })}
    </Box>
  );
}

// ---------------------------------------------------------------------------
// OutOfRangeTable — dialog content listing every out-of-range marker of the
// selected visit with its reference range, position in it, the values of the
// two previous visits and a mini trend line
// ---------------------------------------------------------------------------
const toNum = (v) => parseFloat(String(v ?? '').replace(',', '.'));

// Where the value sits relative to the reference range (bar + dot)
function RangeBar({ value, low, high, optLow, optHigh, optColor, dotColor, trackColor }) {
  const { T } = useTokens();
  const v = toNum(value), lo = toNum(low), hi = toNum(high);
  const oLo = toNum(optLow), oHi = toNum(optHigh);
  if (isNaN(v) || (isNaN(lo) && isNaN(hi))) return null;
  const bounds = [v, lo, hi, oLo, oHi].filter((n) => !isNaN(n));
  const min = Math.min(...bounds);
  const max = Math.max(...bounds);
  const pad = (max - min) * 0.15 || 1;
  const dMin = min - pad, dMax = max + pad;
  const pos = (n) => `${((n - dMin) / (dMax - dMin)) * 100}%`;
  const refStart = isNaN(lo) ? '0%' : pos(lo);
  const refEnd   = isNaN(hi) ? '100%' : pos(hi);
  // A missing optimum bound falls back to the reference bound (as in GaugeBar)
  const hasOpt   = !isNaN(oLo) || !isNaN(oHi);
  const optStart = isNaN(oLo) ? refStart : pos(oLo);
  const optEnd   = isNaN(oHi) ? refEnd : pos(oHi);

  return (
    <Box sx={{ position: 'relative', height: 12, width: '100%' }}>
      <Box sx={{ position: 'absolute', top: 3, left: 0, right: 0, height: 6, borderRadius: 3, bgcolor: trackColor ?? alpha(T.heading, 0.08) }} />
      <Box sx={{
        position: 'absolute', top: 3, height: 6, borderRadius: '2px',
        left: refStart, width: `calc(${refEnd} - ${refStart})`,
        bgcolor: alpha(T.green, 0.22), border: `1px solid ${alpha(T.green, 0.55)}`, boxSizing: 'border-box',
      }} />
      {/* Optimum range — its own colour on top of the green reference band */}
      {hasOpt && (
        <Box sx={{
          position: 'absolute', top: 3, height: 6, borderRadius: '2px',
          left: optStart, width: `calc(${optEnd} - ${optStart})`,
          bgcolor: optColor ?? T.accentText,
        }} />
      )}
      <Box sx={{
        position: 'absolute', top: 0, left: pos(v), transform: 'translateX(-50%)',
        width: 12, height: 12, borderRadius: '50%', bgcolor: dotColor ?? T.red,
      }} />
    </Box>
  );
}

function MiniTrend({ values }) {
  const { T } = useTokens();
  const nums = values.map(toNum);
  if (nums.filter((n) => !isNaN(n)).length < 2) return <Typography sx={{ color: T.body }}>—</Typography>;
  const W = 64, H = 22, PAD = 4;
  const valid = nums.filter((n) => !isNaN(n));
  const min = Math.min(...valid), max = Math.max(...valid), span = max - min || 1;
  const pts = nums
    .map((n, i) => ({ n, x: PAD + (i * (W - PAD * 2)) / (nums.length - 1), y: PAD + ((max - n) / span) * (H - PAD * 2) }))
    .filter((p) => !isNaN(p.n));
  const last = pts[pts.length - 1];
  return (
    <svg width={W} height={H} viewBox={`0 0 ${W} ${H}`} aria-hidden="true" style={{ overflow: 'visible', display: 'block' }}>
      <polyline points={pts.map((p) => `${p.x},${p.y}`).join(' ')} fill="none" stroke={T.body} strokeWidth={1.5} strokeLinejoin="round" />
      {pts.slice(0, -1).map((p, i) => <circle key={i} cx={p.x} cy={p.y} r={2.25} fill={T.body} />)}
      <circle cx={last.x} cy={last.y} r={3.5} fill={T.red} />
    </svg>
  );
}

function OutOfRangeTable({ markers, prevVisits, locale }) {
  const { T } = useTokens();
  const { t } = useTranslation();

  const fmt = (v) => {
    const n = toNum(v);
    return isNaN(n) ? (v ?? '—') : new Intl.NumberFormat(locale, { maximumFractionDigits: 3 }).format(n);
  };
  const markerLabel = (r) => {
    const key = `markername_${r.marker_short}`;
    return r.marker_short && t(key) !== key ? t(key) : (r.marker_name ?? r.marker_short);
  };
  const subsystemLabel = (r) => {
    const sub = r.marker_healthsubsystem?.trim();
    return sub && SUBSYSTEM_KEY[sub] ? t(SUBSYSTEM_KEY[sub]) : sub;
  };
  const refLabel = (r) => {
    const lo = toNum(r.ref_low), hi = toNum(r.ref_high);
    if (!isNaN(lo) && !isNaN(hi)) return `${fmt(r.ref_low)} – ${fmt(r.ref_high)}`;
    if (!isNaN(hi)) return `< ${fmt(r.ref_high)}`;
    if (!isNaN(lo)) return `> ${fmt(r.ref_low)}`;
    return '—';
  };
  const prevValue = (visit, r) => visit.results?.find((x) => x.marker_short === r.marker_short)?.value;

  const columns = [
    'minmax(170px, 1.6fr)', '96px', '72px', '112px', 'minmax(120px, 1.2fr)',
    ...prevVisits.map(() => '76px'), '72px',
  ].join(' ');
  const headSx = {
    fontSize: '0.68rem', fontWeight: 700, color: T.body,
    textTransform: 'uppercase', letterSpacing: '0.08em', whiteSpace: 'nowrap',
  };
  const numSx = { fontVariantNumeric: 'tabular-nums', textAlign: 'right' };

  return (
    <Box>
      <Typography sx={{ fontSize: '0.9rem', color: T.body, lineHeight: 1.6, maxWidth: '75ch', mb: 3 }}>
        {t('hc_out_intro')}
      </Typography>

      <ScrollX aria-label={t('hc_out_title')} sx={{ mx: { xs: -2, sm: 0 }, px: { xs: 2, sm: 0 } }}>
        <Box role="table" sx={{ minWidth: 820 }}>
          <Box role="row" sx={{
            display: 'grid', gridTemplateColumns: columns, columnGap: 2, alignItems: 'end',
            pb: 1.25, borderBottom: `2px solid ${T.heading}`,
          }}>
            <Typography role="columnheader" sx={headSx}>{t('hc_out_col_marker')}</Typography>
            <Typography role="columnheader" sx={{ ...headSx, textAlign: 'right' }}>{t('hc_out_col_value')}</Typography>
            <Typography role="columnheader" sx={headSx}>{t('hc_out_col_unit')}</Typography>
            <Typography role="columnheader" sx={{ ...headSx, textAlign: 'right' }}>{t('hc_out_col_ref')}</Typography>
            <Typography role="columnheader" sx={headSx}>{t('hc_out_col_position')}</Typography>
            {prevVisits.map((pv) => (
              <Typography key={pv.id ?? pv.visit_date} role="columnheader" sx={{ ...headSx, textAlign: 'right' }}>
                {new Date(`${pv.visit_date}T00:00:00`).toLocaleDateString(locale, { month: 'short', year: '2-digit' })}
              </Typography>
            ))}
            <Typography role="columnheader" sx={headSx}>{t('hc_out_col_trend')}</Typography>
          </Box>

          {markers.map((r) => {
            const isHigh = String(r.status).toUpperCase().includes('HIGH');
            return (
              <Box key={r.id ?? r.marker_short} role="row" sx={{
                display: 'grid', gridTemplateColumns: columns, columnGap: 2, alignItems: 'center',
                py: 1.5, borderBottom: `1px solid ${T.sectionLine}`,
              }}>
                <Box role="cell" sx={{ minWidth: 0 }}>
                  <Typography sx={{ fontSize: '0.95rem', fontWeight: 500, color: T.heading, lineHeight: 1.3 }}>
                    {markerLabel(r)}
                  </Typography>
                  {subsystemLabel(r) && (
                    <Typography sx={{ fontSize: '0.8rem', color: T.body, lineHeight: 1.4 }}>{subsystemLabel(r)}</Typography>
                  )}
                </Box>
                <Box role="cell" sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.75, color: T.red }}>
                  <Typography sx={{ ...numSx, fontSize: '1rem', fontWeight: 700, color: 'inherit' }}>{fmt(r.value)}</Typography>
                  <Triangle size={10} fill="currentColor" strokeWidth={0}
                    style={{ transform: isHigh ? 'none' : 'rotate(180deg)', flexShrink: 0 }}
                    aria-label={isHigh ? t('bt_status_high') : t('bt_status_low')} />
                </Box>
                <Typography role="cell" sx={{ fontSize: '0.85rem', color: T.body }}>{r.unit || '—'}</Typography>
                <Typography role="cell" sx={{ ...numSx, fontSize: '0.85rem', color: T.body, whiteSpace: 'nowrap' }}>{refLabel(r)}</Typography>
                <Box role="cell"><RangeBar value={r.value} low={r.ref_low} high={r.ref_high} /></Box>
                {prevVisits.map((pv) => (
                  <Typography key={pv.id ?? pv.visit_date} role="cell" sx={{ ...numSx, fontSize: '0.9rem', color: T.bodyStrong }}>
                    {prevValue(pv, r) != null ? fmt(prevValue(pv, r)) : '—'}
                  </Typography>
                ))}
                <Box role="cell">
                  <MiniTrend values={[...prevVisits.map((pv) => prevValue(pv, r)), r.value]} />
                </Box>
              </Box>
            );
          })}
        </Box>
      </ScrollX>
    </Box>
  );
}

// ---------------------------------------------------------------------------
// ReportCard — full-width first card: the latest speak23 report (PDF) to
// download or read as a booklet. Hidden until a report exists.
// ---------------------------------------------------------------------------
// Signed file URLs expire after 15 minutes, so every open/download asks again
const fetchLatestSpeak23 = async () => {
  const { data } = await axios.get(`${API_URL}/api/labreports/my/`);
  return (Array.isArray(data) ? data : [])
    .filter((v) => v.speak23_report?.file_url)
    .sort((a, b) => String(b.visit_date).localeCompare(String(a.visit_date)))[0] ?? null;
};

function ReportCard() {
  const { T } = useTokens();
  const theme = useTheme();
  const { t, language } = useTranslation();
  const [visit, setVisit] = useState(null);
  const [readOpen, setReadOpen] = useState(false);
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    fetchLatestSpeak23().then(setVisit).catch(() => {});
  }, []);

  const loadUrl = useCallback(
    async () => (await fetchLatestSpeak23())?.speak23_report?.file_url ?? null,
    [],
  );

  if (!visit) return null;

  const locale = language === 'de' ? 'de-DE' : 'en-GB';
  const visitDate = new Date(`${visit.visit_date}T00:00:00`)
    .toLocaleDateString(locale, { day: '2-digit', month: '2-digit', year: 'numeric' });

  const handleDownload = async () => {
    setDownloading(true);
    let url = null;
    try {
      url = await loadUrl();
      // Blob download keeps the user on the page and sets a readable filename
      const res = await fetch(url);
      if (!res.ok) throw new Error(res.status);
      const href = URL.createObjectURL(await res.blob());
      const a = document.createElement('a');
      a.href = href;
      a.download = `speak23_${visit.visit_date}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(href);
    } catch {
      // e.g. the bucket blocks cross-origin fetches → let the browser open it
      if (url) window.open(url, '_blank', 'noopener,noreferrer');
    } finally {
      setDownloading(false);
    }
  };

  const roundBtnSx = {
    width: 40, height: 40, borderRadius: '50%', flexShrink: 0,
    transition: `background-color 150ms ease, filter 150ms ease, transform 160ms ${EASE_OUT}`,
    '& .hc-chip-arrow': { transition: `transform 200ms ${EASE_OUT}` },
    '&:active': { transform: 'scale(0.94)' },
  };

  return (
    <Box sx={{
      ...glassTileSx(T, 0),
      flexDirection: 'row', alignItems: 'center', gap: { xs: 1.5, sm: 2 },
      py: { xs: 1.75, sm: 2 },
    }}>
      <Box sx={{
        width: 44, height: 44, borderRadius: '12px', flexShrink: 0,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        bgcolor: T.chipBg, color: T.heading,
      }}>
        <FileText size={20} strokeWidth={2.25} />
      </Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography component="h2" sx={{ fontSize: { xs: '0.95rem', sm: '1.05rem' }, fontWeight: 600, lineHeight: 1.3, color: T.heading }}>
          {t('hc_report_title')}
        </Typography>
        <Typography sx={{ fontSize: '0.8rem', color: T.body, fontVariantNumeric: 'tabular-nums' }}>
          {t('hc_stats_visit_date')} {visitDate}
        </Typography>
      </Box>

      {/* Download */}
      <Tooltip title={t('hc_report_download')} placement="top">
        <span>
          <ButtonBase
            onClick={handleDownload}
            disabled={downloading}
            aria-label={t('hc_report_download')}
            sx={{
              ...roundBtnSx, color: T.heading,
              border: `1px solid ${T.cardBorder}`,
              '@media (hover: hover) and (pointer: fine)': {
                '&:hover': { bgcolor: alpha(T.heading, T.dark ? 0.06 : 0.045) },
                '&:hover .hc-chip-arrow': { transform: 'translateY(2px)' },
              },
              '&.Mui-focusVisible': { outline: `2px solid ${T.arrow}`, outlineOffset: 2 },
            }}
          >
            {downloading
              ? <CircularProgress size={18} color="inherit" />
              : <Box className="hc-chip-arrow" sx={{ display: 'flex' }}><Download size={18} strokeWidth={2.5} /></Box>}
          </ButtonBase>
        </span>
      </Tooltip>

      {/* Read as booklet */}
      <Tooltip title={t('hc_report_read')} placement="top">
        <ButtonBase
          onClick={() => setReadOpen(true)}
          aria-label={t('hc_report_read')}
          sx={{
            ...roundBtnSx, color: '#fff',
            bgcolor: theme.palette.greydark.C900,
            border: T.dark ? '1px solid rgba(255,255,255,0.14)' : 'none',
            '@media (hover: hover) and (pointer: fine)': {
              '&:hover': { filter: 'brightness(1.2)' },
              '&:hover .hc-chip-arrow': { transform: 'translate(2px, -2px)' },
            },
            '&.Mui-focusVisible': { outline: `2px solid ${theme.palette.greydark.C900}`, outlineOffset: 2 },
          }}
        >
          <Box className="hc-chip-arrow" sx={{ display: 'flex' }}>
            <ArrowUpRight size={18} strokeWidth={2.75} />
          </Box>
        </ButtonBase>
      </Tooltip>

      <CustomDialog
        open={readOpen}
        onClose={() => setReadOpen(false)}
        paperMaxWidth="1400px"
        title={t('hc_report_title')}
        content={readOpen && <ReportBooklet loadUrl={loadUrl} />}
      />
    </Box>
  );
}

// ---------------------------------------------------------------------------
// OutOfRangePie — ring of a visit's rated markers (out of range in red) with
// "out / total" in the centre; the arrow opens every out-of-range marker with
// its history. Used by the lab card here and on the Bloodtests page.
// ---------------------------------------------------------------------------
export function OutOfRangePie({ visit, visits, height = { xs: 160, sm: 170 } }) {
  const { T } = useTokens();
  const { t, language } = useTranslation();
  const [outOpen, setOutOpen] = useState(false);
  const reduceMotion = useMediaQuery('(prefers-reduced-motion: reduce)');

  const rated = (visit?.results ?? []).filter((r) => r.status);
  if (!rated.length) return null;

  const statusOf = (r) => String(r.status).toUpperCase();
  const normal = rated.filter((r) => statusOf(r) === 'NORMAL').length;
  const out    = rated.filter((r) => statusOf(r) !== 'NORMAL');
  const locale = language === 'de' ? 'de-DE' : 'en-GB';

  // Up to two earlier completed visits, oldest first, for the dialog's history columns
  const prevVisits = (visits ?? [])
    .filter((v) => Number(v.state) === 6 && v.visit_date < visit.visit_date)
    .sort((a, b) => new Date(b.visit_date) - new Date(a.visit_date))
    .slice(0, 2)
    .reverse();
  const outSorted = [...out].sort((a, b) =>
    String(a.marker_category ?? '').localeCompare(String(b.marker_category ?? ''))
    || String(a.marker_healthsubsystem ?? '').localeCompare(String(b.marker_healthsubsystem ?? ''))
    || (a.marker_order ?? 0) - (b.marker_order ?? 0));

  // Pie: in range (calm) vs out of range (red)
  const inColor = alpha(T.green, T.dark ? 0.45 : 0.35);
  const pieData = [{ key: 'in', value: normal }, { key: 'out', value: out.length }];

  return (
    <>
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', minWidth: 0 }}>
          <Box
            role="img"
            aria-label={`${out.length} / ${rated.length} ${t('hc_stats_markers')} ${t('hc_stats_out_range')}`}
            sx={{ position: 'relative', width: '100%', height }}
          >
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={pieData}
                  dataKey="value"
                  startAngle={90}
                  endAngle={-270}
                  innerRadius="74%"
                  outerRadius="92%"
                  paddingAngle={out.length && normal ? 4 : 0}
                  cornerRadius={8}
                  stroke="none"
                  isAnimationActive={!reduceMotion}
                  animationDuration={700}
                  animationEasing="ease-out"
                >
                  <Cell fill={inColor} />
                  <Cell fill={T.red} />
                </Pie>
              </PieChart>
            </ResponsiveContainer>

            {/* Centre: out of range / total */}
            <Box sx={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', pointerEvents: 'none' }}>
              <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.25 }}>
                <Typography component="span" sx={{ fontFamily: "'Poppins', sans-serif", fontSize: { xs: '1.6rem', sm: '1.85rem' }, fontWeight: 600, lineHeight: 1, letterSpacing: '-0.02em', color: out.length ? T.red : T.green, fontVariantNumeric: 'tabular-nums' }}>
                  {out.length}
                </Typography>
                <Typography component="span" sx={{ fontFamily: "'Poppins', sans-serif", fontSize: { xs: '1rem', sm: '1.1rem' }, fontWeight: 500, color: T.body, fontVariantNumeric: 'tabular-nums' }}>
                  /{rated.length}
                </Typography>
              </Box>
              {/* Arrow → out-of-range dialog, below the numbers */}
              {out.length > 0 && (
                  <Tooltip title={t('hc_out_open')} placement="bottom">
                    <ButtonBase
                      onClick={() => setOutOpen(true)}
                      aria-label={t('hc_out_open')}
                      sx={{
                        mt: 0.75, pointerEvents: 'auto',
                        width: 28, height: 28, borderRadius: '50%', color: T.red,
                        bgcolor: T.redBg, border: `1px solid ${T.redBorder}`,
                        transition: `background-color 150ms ease, transform 160ms ${EASE_OUT}`,
                        '& .hc-chip-arrow': { transition: `transform 200ms ${EASE_OUT}` },
                        '@media (hover: hover) and (pointer: fine)': {
                          '&:hover': { bgcolor: alpha(T.red, T.dark ? 0.24 : 0.14) },
                          '&:hover .hc-chip-arrow': { transform: 'translate(1px, -1px)' },
                        },
                        '&:active': { transform: 'scale(0.94)' },
                        '&.Mui-focusVisible': { outline: `2px solid ${T.red}`, outlineOffset: 2 },
                      }}
                    >
                      <Box className="hc-chip-arrow" sx={{ display: 'flex' }}>
                        <ArrowUpRight size={15} strokeWidth={2.75} />
                      </Box>
                    </ButtonBase>
                  </Tooltip>
              )}
            </Box>
          </Box>

        </Box>

      <CustomDialog
        open={outOpen}
        onClose={() => setOutOpen(false)}
        paperMaxWidth="1080px"
        title={t('hc_out_title')}
        content={<OutOfRangeTable markers={outSorted} prevVisits={prevVisits} locale={locale} />}
      />
    </>
  );
}

// ---------------------------------------------------------------------------
// LabStatsCard — next to the BioAge tile: date and lab of the selected visit
// on the left half, a pie of all tested markers (out of range in red) on the right
// ---------------------------------------------------------------------------
function LabStatsCard({ visit, visits, dates = [], selectedDate, onSelectDate }) {
  const { T } = useTokens();
  const theme = useTheme();
  const { t, language } = useTranslation();
  const navigate = useNavigate();
  const [dateMenu, setDateMenu] = useState(null); // anchor of the date dropdown
  const [planOption, setPlanOption] = useState(null);

  useEffect(() => {
    axios
      .get(`${API_URL}/customer-profile/me/`)
      .then(({ data }) => setPlanOption(data.plan_option ?? null))
      .catch(() => {});
  }, []);

  if (!visit) return null;

  const locale  = language === 'de' ? 'de-DE' : 'en-GB';
  const visitDate = new Date(`${visit.visit_date}T00:00:00`)
    .toLocaleDateString(locale, { day: '2-digit', month: '2-digit', year: 'numeric' });

  // Proposed next visit (nothing booked yet): latest completed visit + plan interval
  // (plan_option 2/4 = 2x per year → 6 months, 3/5 = 4x per year → 3 months)
  const plan = Number(planOption);
  const intervalMonths = [2, 4].includes(plan) ? 6 : [3, 5].includes(plan) ? 3 : null;
  const lastVisitDate = (visits ?? [])
    .filter((v) => Number(v.state) === 6 && v.visit_date)
    .map((v) => v.visit_date)
    .sort()
    .pop();
  // Booked next visit: a visit past the appointment step (state not 1 =
  // available, not 6 = completed) carries the date the user selected
  const bookedVisitDate = (visits ?? [])
    .filter((v) => ![1, 6].includes(Number(v.state)) && v.visit_date)
    .map((v) => v.visit_date)
    .sort()[0];
  let nextVisitDate = null;
  let nextVisitOverdue = false;
  if (bookedVisitDate) {
    nextVisitDate = new Date(`${bookedVisitDate}T00:00:00`)
      .toLocaleDateString(locale, { day: '2-digit', month: '2-digit', year: 'numeric' });
  } else if (intervalMonths && lastVisitDate) {
    const d = new Date(`${lastVisitDate}T00:00:00`);
    d.setMonth(d.getMonth() + intervalMonths);
    nextVisitDate = d.toLocaleDateString(locale, { day: '2-digit', month: '2-digit', year: 'numeric' });
    nextVisitOverdue = d < new Date(new Date().setHours(0, 0, 0, 0));
  }
  const labelSx = {
    fontSize: { xs: '0.68rem', sm: '0.75rem' }, fontWeight: 700, color: T.body,
    textTransform: 'uppercase', letterSpacing: '0.08em', lineHeight: 1.3,
  };
  // Date chip: greydark surface, white text (chip radius shared with all pills)
  const dateChipSx = {
    mt: 0.75, px: 1.25, py: 0.5, gap: 0.5, alignItems: 'center',
    borderRadius: CHIP_RADIUS,
    bgcolor: theme.palette.greydark.C900,
    border: T.dark ? '1px solid rgba(255,255,255,0.14)' : 'none',
  };
  const dateChipTextSx = {
    fontSize: { xs: '0.88rem', sm: '0.95rem' }, fontWeight: 600, lineHeight: 1.3,
    color: '#fff', fontVariantNumeric: 'tabular-nums',
  };

  return (
    <Box sx={{
      ...glassTileSx(T, 1),
      position: 'relative',
      gridColumn: { xs: '1 / -1', md: 'span 3' },
      // Takes the whole row when the BioAge tile isn't shown
      '&:only-child': { gridColumn: '1 / -1' },
      px: { xs: 2.25, sm: 3 }, pt: { xs: 2.25, sm: 2.75 }, pb: { xs: 2.25, sm: 2.5 },
      display: 'grid',
      gridTemplateColumns: { xs: '1fr', sm: 'minmax(0, 1fr) minmax(0, 1fr)' },
      columnGap: 3, rowGap: 2.5, alignItems: 'center',
    }}>
      {/* Link to the lab report, top right */}
      <Tooltip title={t('hc_stats_to_report')} placement="top">
        <ButtonBase
          onClick={() => navigate('/BloodtestsWelcome')}
          sx={{
            ...linkButtonSx(T),
            // zIndex: stays clickable above the pie, which fills the right half
            position: 'absolute', top: { xs: 10, sm: 14 }, right: { xs: 10, sm: 14 }, zIndex: 1,
          }}
        >
          {t('hc_stats_report_link')}
          <ArrowUpRight size={20} strokeWidth={3} />
        </ButtonBase>
      </Tooltip>

      {/* Left: visit date + proposed next visit */}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: { xs: 1.75, sm: 2.5 }, alignSelf: 'start', pr: 4, minWidth: 0 }}>
        <Box>
          <Typography component="h3" sx={labelSx}>{t('hc_stats_visit_date')}</Typography>
          {dates.length > 1 ? (
            <>
              {/* Date dropdown — replaces the date carousel at the top of the page */}
              <ButtonBase
                onClick={(e) => setDateMenu(e.currentTarget)}
                aria-haspopup="listbox"
                aria-expanded={Boolean(dateMenu)}
                aria-label={`${t('hc_stats_visit_date')}: ${visitDate}`}
                sx={{
                  // Greydark chip (same surface as the BioAge tile) so the
                  // date reads as the page's date control
                  ...dateChipSx, pr: 1,
                  transition: `filter 150ms ease, transform 160ms ${EASE_OUT}`,
                  '@media (hover: hover) and (pointer: fine)': {
                    '&:hover': { filter: 'brightness(1.2)' },
                  },
                  '&:active': { transform: 'scale(0.97)' },
                  '&.Mui-focusVisible': { outline: `2px solid ${theme.palette.greydark.C900}`, outlineOffset: 2 },
                }}
              >
                <Typography component="time" dateTime={visit.visit_date} sx={dateChipTextSx}>
                  {visitDate}
                </Typography>
                <Box sx={{
                  display: 'flex', color: alpha('#fff', 0.8),
                  transform: dateMenu ? 'rotate(180deg)' : 'none',
                  transition: `transform 200ms ${EASE_OUT}`,
                }}>
                  <ChevronDown size={18} strokeWidth={2.5} />
                </Box>
              </ButtonBase>
              <Menu
                anchorEl={dateMenu}
                open={Boolean(dateMenu)}
                onClose={() => setDateMenu(null)}
                transitionDuration={150}
                slotProps={{ paper: { sx: { mt: 0.5, borderRadius: '14px', minWidth: 180 } }, list: { role: 'listbox', dense: true } }}
              >
                {dates.map((d) => {
                  const active = d === selectedDate;
                  return (
                    <MenuItem
                      key={d}
                      role="option"
                      aria-selected={active}
                      selected={active}
                      onClick={() => { setDateMenu(null); if (!active) onSelectDate?.(d); }}
                      sx={{ gap: 1.5, justifyContent: 'space-between', fontVariantNumeric: 'tabular-nums', fontWeight: active ? 700 : 400 }}
                    >
                      {new Date(`${d}T00:00:00`).toLocaleDateString(locale, { day: '2-digit', month: '2-digit', year: 'numeric' })}
                      {active && <Check size={16} strokeWidth={2.5} />}
                    </MenuItem>
                  );
                })}
              </Menu>
            </>
          ) : (
            <Box sx={{ ...dateChipSx, display: 'inline-flex' }}>
              <Typography component="time" dateTime={visit.visit_date} sx={dateChipTextSx}>
                {visitDate}
              </Typography>
            </Box>
          )}
        </Box>
        {nextVisitDate && (
          <Box>
            <Typography component="h3" sx={labelSx}>{t('hc_stats_next_visit')}</Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
              {/* Next visit chip → lab visits page to book it */}
              <Tooltip title={t('hc_stats_book_visit')} placement="top">
                <ButtonBase
                  onClick={() => navigate('/LabVisitsWelcome')}
                  aria-label={`${t('hc_stats_next_visit')}: ${nextVisitDate} — ${t('hc_stats_book_visit')}`}
                  sx={{
                    ...dateChipSx, pr: 1,
                    transition: `filter 150ms ease, transform 160ms ${EASE_OUT}`,
                    '& .hc-chip-arrow': { transition: `transform 200ms ${EASE_OUT}` },
                    '@media (hover: hover) and (pointer: fine)': {
                      '&:hover': { filter: 'brightness(1.2)' },
                      '&:hover .hc-chip-arrow': { transform: 'translateX(2px)' },
                    },
                    '&:active': { transform: 'scale(0.97)' },
                    '&.Mui-focusVisible': { outline: `2px solid ${theme.palette.greydark.C900}`, outlineOffset: 2 },
                  }}
                >
                  <Typography sx={dateChipTextSx}>{nextVisitDate}</Typography>
                  <Box className="hc-chip-arrow" sx={{ display: 'flex', color: alpha('#fff', 0.8) }}>
                    <ArrowRight size={18} strokeWidth={2.5} />
                  </Box>
                </ButtonBase>
              </Tooltip>
              {/* Reminder: the proposed date has already passed */}
              {nextVisitOverdue && (
                <Tooltip title={t('hc_stats_next_visit_overdue')} placement="top">
                  <Box
                    role="img"
                    aria-label={t('hc_stats_next_visit_overdue')}
                    sx={{ mt: 0.75, display: 'flex', color: T.amber }}
                  >
                    <AlarmClock size={18} strokeWidth={2.5} />
                  </Box>
                </Tooltip>
              )}
            </Box>
          </Box>
        )}
      </Box>

      {/* Right half: out-of-range pie */}
      <Box sx={{ minWidth: 0, pr: { sm: 6 } }}>
        <OutOfRangePie visit={visit} visits={visits} />
      </Box>
    </Box>
  );
}

// ---------------------------------------------------------------------------
// TopTopicCard — the mechanism with the highest risk score: title with its
// score chip and a details button, and the 10-segment score bar
// ---------------------------------------------------------------------------
function TopTopicCard({ pillars, markersMap, visitResults, summary = [] }) {
  const { T, RISK_CONFIG } = useTokens();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [detailsOpen, setDetailsOpen] = useState(false);

  const mechanisms = pillars.flatMap((pl) => pl.mechanism_scores ?? []);
  if (!mechanisms.length) return null;
  // Highest score wins; on a tie the earlier mechanism (lower number) stays
  const top = mechanisms.reduce((best, ms) => (Number(ms.risk_score) > Number(best.risk_score) ? ms : best));
  const score = Number(top.risk_score) || 0;
  const level = getRiskLevel(score);
  const cfg = RISK_CONFIG[level];
  const levelBg = { low: T.greenBg, med: T.amberBg, high: T.redBg }[level];
  const levelBorder = { low: T.greenBorder, med: T.amberBorder, high: T.redBorder }[level];

  const titleKey = mechLookup(MECHANISM_TITLE_KEYS, top.mechanism);
  const label = top.title ?? (titleKey ? t(titleKey) : top.mechanism);

  // "Das Wichtigste in Kürze" bullet about this mechanism: the first one whose
  // title matches the mechanism (exact, else one containing the other); if none
  // matches, the first bullet — the report leads with the biggest topic
  const norm = (v) => String(v ?? '').trim().toLowerCase();
  const want = norm(label);
  const summaryItem = summary.find((it) => norm(it.title) === want)
    ?? summary.find((it) => want && norm(it.title) && (norm(it.title).includes(want) || want.includes(norm(it.title))))
    ?? summary[0];

  // Markers for the details dialog — same rules as MobileMechanismRow
  const { attention, evaluated } = mechanismMarkers(top, markersMap, t);

  return (
    <Box sx={{
      ...glassTileSx(T, 2),
      px: { xs: 2.25, sm: 3 }, pt: { xs: 2.25, sm: 2.75 }, pb: { xs: 2.25, sm: 3 },
    }}>
      {/* Label + link to all 12 mechanisms */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, minWidth: 0 }}>
          <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: cfg.dot, flexShrink: 0 }} />
          <Typography component="h2" sx={{
            fontSize: '0.75rem', fontWeight: 700, color: T.bodyStrong,
            textTransform: 'uppercase', letterSpacing: '0.08em', lineHeight: 1.3,
          }}>
            {t('hc_top_label')}
          </Typography>
        </Box>
        <ButtonBase onClick={() => navigate('/MechanismsWelcome')} sx={{ ...linkButtonSx(T), mr: -0.5, flexShrink: 0 }}>
          {t('hc_top_all')}
          <ArrowUpRight size={20} strokeWidth={3} />
        </ButtonBase>
      </Box>

      {/* Title + score chip + details button */}
      <Box sx={{ mt: 1.5, display: 'flex', alignItems: 'center', flexWrap: 'wrap', columnGap: 1.5, rowGap: 1 }}>
        <Typography sx={{ fontSize: { xs: '1.35rem', sm: '1.7rem' }, fontWeight: 700, color: T.heading, lineHeight: 1.2, letterSpacing: '-0.01em' }}>
          {label}
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Box sx={{ ...CHIP_SX, bgcolor: levelBg, border: `1px solid ${levelBorder}` }}>
            <Typography sx={{ ...CHIP_TEXT_SX, color: cfg.dot, whiteSpace: 'nowrap' }}>
              {t('hc_top_score').replace('{s}', score)}
            </Typography>
          </Box>
          <Tooltip title={t('hc_details')} placement="top">
            <IconButton size="small" aria-label={`${label} — ${t('hc_details')}`} onClick={() => setDetailsOpen(true)}
              sx={{ color: T.arrow, p: 0.5 }}>
              <ArrowUpRight size={22} strokeWidth={3} />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* 10-segment score bar */}
      <Box aria-hidden="true" sx={{ mt: 2, display: 'grid', gridTemplateColumns: 'repeat(10, 1fr)', gap: { xs: '5px', sm: '8px' } }}>
        {Array.from({ length: 10 }).map((_, i) => (
          <Box key={i} sx={{ height: 8, borderRadius: '4px', bgcolor: i < score ? cfg.dot : alpha(T.heading, T.dark ? 0.12 : 0.08) }} />
        ))}
      </Box>

      {/* Key takeaway for this mechanism (from "Das Wichtigste in Kürze") */}
      {summaryItem?.text && (
        <Typography sx={{ mt: 2.25, fontSize: { xs: '0.9rem', sm: '0.95rem' }, color: T.bodyStrong, lineHeight: 1.65, maxWidth: '75ch' }}>
          {summaryItem.text}
        </Typography>
      )}

      <CustomDialog
        open={detailsOpen}
        onClose={() => setDetailsOpen(false)}
        paperMaxWidth="1160px"
        title=""
        content={
          <MechanismDetails
            number={mechanisms.indexOf(top) + 1}
            label={label}
            shortDescription={top.short_description}
            score={score}
            description={top.interpretation}
            attention={attention}
            evaluated={evaluated}
            visitResults={visitResults}
          />
        }
      />
    </Box>
  );
}

// One value block for RecommendationsSummaryCard: small caps label, large
// tabular number, quiet unit
function GoalStat({ label, op, value, unit, color, mutedColor }) {
  const { T } = useTokens();
  const muted = mutedColor ?? T.body;
  return (
    <Box sx={{ minWidth: 0 }}>
      <Typography sx={{ fontSize: '0.66rem', fontWeight: 700, color: muted, textTransform: 'uppercase', letterSpacing: '0.08em' }}>
        {label}
      </Typography>
      <Box sx={{ mt: 0.5, display: 'flex', alignItems: 'baseline', gap: 0.5, flexWrap: 'wrap' }}>
        <Typography component="span" sx={{
          fontFamily: "'Poppins', sans-serif", fontWeight: 600, lineHeight: 1,
          fontSize: { xs: '1.45rem', sm: '1.75rem' }, letterSpacing: '-0.02em',
          fontVariantNumeric: 'tabular-nums', color, whiteSpace: 'nowrap',
        }}>
          {op && <Box component="span" sx={{ fontWeight: 400, opacity: 0.7, mr: '0.15em' }}>{op}</Box>}
          {value}
        </Typography>
        {/* Breakpoint object so it beats body1's per-breakpoint @media fontSize in the theme */}
        {unit && <Typography component="span" sx={{ fontSize: { xs: '0.68rem' }, color: muted, whiteSpace: 'nowrap' }}>{unit}</Typography>}
      </Box>
    </Box>
  );
}

// ---------------------------------------------------------------------------
// RecommendationsSummaryCard — the top 3 recommendations, each with what to do
// and its goal (current value of the target marker → target value)
// ---------------------------------------------------------------------------
function RecommendationsSummaryCard({ recs, results, routine }) {
  const { T } = useTokens();
  const theme = useTheme();
  // Secondary text on the dark goal boxes
  const onDarkMuted = alpha('#fff', 0.62);
  const { t, language } = useTranslation();
  const navigate = useNavigate();
  // Index of the recommendation whose goal text is shown in the dialog
  const [goalOpen, setGoalOpen] = useState(null);
  if (!recs.length) return null;

  const locale = language === 'de' ? 'de-DE' : 'en-GB';
  const num = (v) => parseFloat(String(v ?? '').replace(',', '.'));
  const fmt = (v) => (isNaN(num(v)) ? v : new Intl.NumberFormat(locale, { maximumFractionDigits: 2 }).format(num(v)));
  const norm = (s) => String(s ?? '').toLowerCase().split(' (')[0].trim();

  // target_marker is a display name ("Gesamtcholesterin") — match it against
  // the translated or backend marker names of the visit's results
  const findMarker = (name) => {
    if (!name) return null;
    const want = norm(name);
    return results.find((r) => {
      const key = `markername_${r.marker_short}`;
      const translated = t(key) !== key ? t(key) : null;
      return [translated, r.marker_name, r.marker_short].some((n) => n && norm(n) === want);
    }) ?? null;
  };
  // Out of the reference range, the goal is to get back into it: target is
  // the side of the reference range the value is off
  const refTargetOf = (r) => {
    const isLow = String(r.status).toUpperCase().includes('LOW');
    const lo = num(r.ref_low), hi = num(r.ref_high);
    if (isLow && !isNaN(lo)) return { op: '>', value: fmt(r.ref_low) };
    if (!isNaN(hi)) return { op: '<', value: fmt(r.ref_high) };
    if (!isNaN(lo)) return { op: '>', value: fmt(r.ref_low) };
    return null;
  };
  // Within the reference range, the goal is the optimum range: the side the
  // value is off, or the full range (op = null) when it is already inside it
  const optTargetOf = (r) => {
    const v = num(r.value), lo = num(r.opt_low), hi = num(r.opt_high);
    if (!isNaN(lo) && !isNaN(hi)) {
      if (v < lo) return { op: '>', value: fmt(r.opt_low) };
      if (v > hi) return { op: '<', value: fmt(r.opt_high) };
      return { op: null, value: `${fmt(r.opt_low)}–${fmt(r.opt_high)}` };
    }
    if (!isNaN(hi)) return { op: '<', value: fmt(r.opt_high) };
    if (!isNaN(lo)) return { op: '>', value: fmt(r.opt_low) };
    return null;
  };


  const labelSx = {
    fontSize: '0.7rem', fontWeight: 700, color: T.body,
    textTransform: 'uppercase', letterSpacing: '0.08em', lineHeight: 1.3,
  };

  return (
    <Box sx={{
      borderRadius: '28px',
      bgcolor: T.dark ? alpha('#fff', 0.04) : alpha('#fff', 0.5),
      backdropFilter: 'blur(28px) saturate(180%)',
      WebkitBackdropFilter: 'blur(28px) saturate(180%)',
      border: `1px solid ${T.dark ? 'rgba(255,255,255,0.08)' : 'rgba(255,255,255,0.65)'}`,
      boxShadow: T.dark
        ? 'inset 0 1px 0 rgba(255,255,255,0.06), 0 8px 32px rgba(0,0,0,0.55)'
        : 'inset 0 1px 0 rgba(255,255,255,0.7), 0 4px 24px rgba(0,0,0,0.06)',
      px: { xs: 2.25, sm: 3 }, pt: { xs: 2.25, sm: 2.75 }, pb: { xs: 1, sm: 1.5 },
      animation: `hc-fade-up 0.5s ${EASE_OUT} 0.12s both`,
      '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
    }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
        <Typography component="h2" sx={{ ...labelSx, fontSize: '0.75rem', color: T.bodyStrong }}>
          {t('hc_recs_label').replace('{n}', recs.length)}
        </Typography>
        <Tooltip title={t('hc_recs_open')} placement="top">
          <ButtonBase onClick={() => navigate('/RecommendationsWelcome')} sx={{ ...linkButtonSx(T), mr: -0.5, flexShrink: 0 }}>
            {t('hc_recs_details')}
            <ArrowUpRight size={20} strokeWidth={3} />
          </ButtonBase>
        </Tooltip>
      </Box>

      {/* The report's routine text (same as "Deine Routine für die nächsten
          Monate" on the recommendations page) as intro to the list */}
      {routine && (
        <Typography sx={{
          mt: 1, pb: { xs: 2, sm: 2.25 }, borderBottom: `1px solid ${T.sectionLine}`,
          fontSize: { xs: '0.88rem', sm: '0.92rem' }, color: T.body, lineHeight: 1.7,
          whiteSpace: 'pre-line', maxWidth: '75ch',
        }}>
          {routine}
        </Typography>
      )}

      {recs.map((rec, i) => {
        const marker = findMarker(rec.target_marker);
        const outOfRange = marker && String(marker.status).toUpperCase() !== 'NORMAL';
        const target = marker && (outOfRange ? refTargetOf(marker) : optTargetOf(marker));
        return (
          <Box key={rec.id ?? i} sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: 'minmax(0, 1fr) minmax(0, 1fr)' },
            columnGap: 3, rowGap: 1.75,
            py: { xs: 2.25, sm: 2.5 },
            borderBottom: i < recs.length - 1 ? `1px solid ${T.sectionLine}` : 'none',
          }}>
            {/* Recommendation, numbered with a greydark badge (matches the goal boxes) */}
            <Box sx={{ minWidth: 0 }}>
              <Box aria-hidden="true" sx={{
                width: 26, height: 26, borderRadius: '50%',
                bgcolor: theme.palette.greydark.C900,
                border: T.dark ? '1px solid rgba(255,255,255,0.14)' : 'none',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                <Typography sx={{ fontSize: '0.78rem', fontWeight: 700, color: '#fff', lineHeight: 1, fontVariantNumeric: 'tabular-nums' }}>
                  {i + 1}
                </Typography>
              </Box>
              <Typography sx={{ mt: 1, fontSize: { xs: '0.92rem', sm: '0.98rem' }, color: T.heading, lineHeight: 1.6 }}>
                {rec.what}
              </Typography>
            </Box>

            {(marker || rec.goal) && (
            <Box sx={{ minWidth: 0 }}>
            {/* Ziel — same greydark surface as the BioAge tile */}
            <Box sx={{
              px: { xs: 1.75, sm: 2.25 }, py: { xs: 1.5, sm: 1.75 }, borderRadius: '16px',
              bgcolor: theme.palette.greydark.C900,
              border: `1px solid ${T.dark ? 'rgba(255,255,255,0.1)' : alpha(theme.palette.greydark.C900, 0.9)}`,
            }}>
              {/* Label row: "ZIEL · Gesamtcholesterin" + goal-text button on the right
                  (bloodmarkers only — otherwise the goal text sits in the card) */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minHeight: 24 }}>
                <Typography sx={{ ...labelSx, color: onDarkMuted }}>
                  {t('hc_rec_goal')}
                  {marker && rec.target_marker && (
                    <Box component="span" sx={{ textTransform: 'none', letterSpacing: 0, fontWeight: 500, color: alpha('#fff', 0.88), fontSize: '0.8rem' }}>
                      {'  ·  '}{rec.target_marker}
                    </Box>
                  )}
                </Typography>
                {marker && rec.goal && (
                  <Tooltip title={t('hc_rec_goal')} placement="top">
                    <IconButton size="small" aria-label={`${t('hc_rec_goal')} — Info`} onClick={() => setGoalOpen(i)}
                      sx={{ ml: 'auto', mr: -0.5, color: alpha('#fff', 0.88), p: 0.5, '&:hover': { color: '#fff', bgcolor: alpha('#fff', 0.1) } }}>
                      <MessageSquareText size={20} strokeWidth={2.25} />
                    </IconButton>
                  </Tooltip>
                )}
              </Box>

              {/* Not a bloodmarker (no value, no ranges): show the goal text itself */}
              {!marker && rec.goal && (
                <Typography sx={{ mt: 1, fontSize: { xs: '0.88rem', sm: '0.92rem' }, color: alpha('#fff', 0.88), lineHeight: 1.65 }}>
                  {rec.goal}
                </Typography>
              )}

              {marker && (
                <>
                  {/* Current → target as two stat blocks */}
                  <Box sx={{
                    mt: 1.5,
                    display: 'grid', gridTemplateColumns: target ? 'minmax(0, 1fr) auto minmax(0, 1fr)' : '1fr',
                    alignItems: 'end', columnGap: { xs: 1.25, sm: 2 },
                  }}>
                    <GoalStat
                      label={t('hc_recs_current')}
                      value={fmt(marker.value)}
                      unit={marker.unit}
                      color={outOfRange ? T.red : '#fff'}
                      mutedColor={onDarkMuted}
                    />
                    {target && (
                      <>
                        <Box aria-hidden="true" sx={{
                          mb: '2px', width: 28, height: 28, borderRadius: '50%', flexShrink: 0,
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          bgcolor: alpha('#fff', 0.1), color: alpha('#fff', 0.75),
                        }}>
                          <ArrowRight size={15} strokeWidth={2.5} />
                        </Box>
                        <GoalStat
                          label={t(outOfRange ? 'hc_recs_target' : 'hc_recs_optimal')}
                          op={target.op}
                          value={target.value}
                          unit={marker.unit}
                          color={T.green}
                          mutedColor={onDarkMuted}
                        />
                      </>
                    )}
                  </Box>

                  {/* Where the value sits relative to the target range */}
                  <Box sx={{ mt: 1.75 }}>
                    <RangeBar value={marker.value} low={marker.ref_low} high={marker.ref_high} optLow={marker.opt_low} optHigh={marker.opt_high} optColor={theme.palette.quartiary.C300} dotColor={outOfRange ? T.red : T.green} trackColor={alpha('#fff', 0.14)} />
                  </Box>
                </>
              )}

            </Box>
            </Box>
            )}
          </Box>
        );
      })}

      <CustomDialogSmall
        open={goalOpen != null}
        onClose={() => setGoalOpen(null)}
        title={goalOpen != null ? (recs[goalOpen]?.target_marker || t('hc_rec_goal')) : ''}
        content={
          <Typography sx={{ fontSize: '0.9rem', color: T.body, lineHeight: 1.75 }}>
            {goalOpen != null ? recs[goalOpen]?.goal : ''}
          </Typography>
        }
      />
    </Box>
  );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------
// `section` picks which part of the report this page shows:
// 'mechanisms' (cockpit), 'mechanisms12', 'recommendations' or 'doctor'
export default function HealthCockpit({ section = 'mechanisms' }) {
  const { T } = useTokens();
  const theme = useTheme();
  const { t, language } = useTranslation();
  const { state } = useAuth();
  const userId    = state.user.id;

  const [dates, setDates]               = useState([]);
  const [selectedDate, setSelectedDate] = useState('');
  const [datesLoading, setDatesLoading] = useState(true);
  const [datesError, setDatesError]     = useState(null);

  const [visitResults, setVisitResults] = useState([]);

  const selectedVisit = useMemo(
    () => visitResults.find((v) => v.visit_date === selectedDate && Number(v.state) === 6),
    [visitResults, selectedDate],
  );

  const markersMap = useMemo(() => {
    if (!visitResults.length || !selectedDate) return {};
    const visit = visitResults.find((v) => v.visit_date === selectedDate);
    if (!visit) return {};
    return (visit.results ?? []).reduce((acc, r) => {
      acc[r.marker_short] = r;
      return acc;
    }, {});
  }, [visitResults, selectedDate]);

  const [report, setReport]               = useState(null);
  const [reportLoading, setReportLoading] = useState(false);
  const [reportError, setReportError]     = useState(null);

  // ── 1. Load lab visit dates, select the most recent ────────────────────────
  useEffect(() => {
    let cancelled = false;
    const fetchDates = async () => {
      setDatesLoading(true);
      setDatesError(null);
      try {
        const { data } = await axios.get(`${API_URL}/api/all-lab-visits/results/`);
        if (cancelled) return;
        const labVisits = Array.isArray(data) ? data : (data.lab_visits ?? []);
        setVisitResults(labVisits);
        const sorted = [...new Set(labVisits.filter((v) => Number(v.state) === 6).map((v) => v.visit_date).filter(Boolean))]
          .sort((a, b) => new Date(b) - new Date(a));
        setDates(sorted);
        if (sorted.length > 0) setSelectedDate(sorted[0]);
      } catch {
        if (!cancelled) setDatesError(t('hc_error_generic'));
      } finally {
        if (!cancelled) setDatesLoading(false);
      }
    };
    fetchDates();
    return () => { cancelled = true; };
  }, [userId]);

  // ── 2. Load full report for selected date ──────────────────────────────────
  const fetchReport = useCallback(async (date) => {
    setReportLoading(true);
    setReportError(null);
    setReport(null);
    try {
      const { data } = await axios.get(`${API_URL}/api/reports/${userId}/${date}/`);
      setReport(data);
    } catch (err) {
      const status = err.response?.status;
      // 404 = report not generated yet → falls through to the pending state
      if (status !== 404) {
        setReportError(status === 403 ? t('hc_error_permission') : t('hc_error_generic'));
      }
    } finally {
      setReportLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    if (!selectedDate) return;
    fetchReport(selectedDate);
  }, [selectedDate, fetchReport]);

  // ── Sorted recommendations ─────────────────────────────────────────────────
  const sortedRecs = report?.top_recommendations
    ? [...report.top_recommendations].sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
    : [];

  const pillars = buildPillars(report);

  const hasContent = !!report && (
    section === 'recommendations' ? sortedRecs.length > 0
    : section === 'doctor' ? (report.doctor_notes?.length ?? 0) > 0
    : pillars.length > 0
  );

  // "Report not ready yet" card — whole page on the report pages, in place of
  // the report-based cards on the cockpit
  const pendingCard = (
    <Paper
      elevation={0}
      sx={{
        p: { xs: 4, md: 6 },
        borderRadius: '18px',
        display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2,
        textAlign: 'center',
        bgcolor: T.dark ? alpha('#fff', 0.04) : alpha('#fff', 0.5),
        backdropFilter: 'blur(28px) saturate(180%)',
        WebkitBackdropFilter: 'blur(28px) saturate(180%)',
        border: `1px solid ${T.dark ? 'rgba(255,255,255,0.08)' : 'rgba(255,255,255,0.65)'}`,
        boxShadow: T.dark
          ? 'inset 0 1px 0 rgba(255,255,255,0.06), 0 8px 32px rgba(0,0,0,0.55)'
          : 'inset 0 1px 0 rgba(255,255,255,0.7), 0 4px 24px rgba(0,0,0,0.06)',
        animation: 'hc-fade-up 0.5s ease both',
      }}
    >
      <Hourglass size={28} color={T.overline} />
      <Typography sx={{ fontSize: '1.05rem', fontWeight: 700, color: T.heading, lineHeight: 1.5 }}>
        {t('hc_report_pending')}
      </Typography>
    </Paper>
  );

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <>
      <Global styles={GLOBAL_STYLES} />
      <Box sx={{ maxWidth: '900px', mx: 'auto', px: { xs: 2, sm: 4, md: 6 }, width: '100%', mb: 12 }}>

        {/* Date selector — on the cockpit it lives in the lab-visit card instead */}
        {section === 'mechanisms' ? null : datesLoading ? (
          <DateSkeleton />
        ) : dates.length > 0 && (
          <Box sx={{ mb: 4, display: 'flex', justifyContent: 'center' }}>
            <DateSelector dates={dates} selectedDate={selectedDate} onSelect={setSelectedDate} />
          </Box>
        )}

        {section === 'mechanisms' ? (
          // Cockpit: the lab card (with the date dropdown), BioAge and biometrics
          // don't need the report, so they always render — otherwise a date
          // without a report would leave no way to pick another date
          datesLoading ? <CockpitSkeleton /> : datesError ? (
            <Alert severity="error" sx={{ borderRadius: '12px' }}>{datesError}</Alert>
          ) : (
            // mt: room below the page header now that the date carousel is gone
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, mt: { xs: 2, sm: 3 } }}>
              {/* Full-width speak23 report card (read / download) */}
              <ReportCard />
              {/* BioAge tile + lab-visit card (spans the other 3 columns) */}
              <Box sx={TILE_GRID_SX}>
                <BioAgeCard selectedDate={selectedDate} />
                <LabStatsCard
                  visit={selectedVisit}
                  visits={visitResults}
                  dates={dates}
                  selectedDate={selectedDate}
                  onSelectDate={setSelectedDate}
                />
              </Box>
              {reportLoading ? (
                <Skeleton variant="rounded" height={150} sx={{ borderRadius: '28px' }} />
              ) : reportError ? (
                <Alert severity="error" sx={{ borderRadius: '12px' }}>{reportError}</Alert>
              ) : report && pillars.length > 0 ? (
                <TopTopicCard pillars={pillars} markersMap={markersMap} visitResults={visitResults} summary={report.executive_summary ?? []} />
              ) : pendingCard}
              <BiometricsRow />
              {!reportLoading && !reportError && report && sortedRecs.length > 0 && (
                <RecommendationsSummaryCard recs={sortedRecs.slice(0, 3)} results={selectedVisit?.results ?? []} routine={report.recommendations_routine} />
              )}
            </Box>
          )
        ) : datesLoading || reportLoading ? <CockpitSkeleton /> : (datesError || reportError) ? (
          <Alert severity="error" sx={{ borderRadius: '12px' }}>{datesError || reportError}</Alert>
        ) : hasContent ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 8 }}>

            {/* PART 1b — The 12 mechanisms (own page) */}
            {section === 'mechanisms12' && (
            <Box>
              <MechanismsHeader pillars={pillars} />

              {/* One card per area, 3 mechanisms each, numbered 1–12 */}
              <PillarMechanismsCard
                pillars={pillars}
                markersMap={markersMap}
                visitResults={visitResults}
              />

              <ExecSummary items={report.executive_summary ?? []} pillars={pillars} />
            </Box>
            )}

            {/* PART 2 — Top Recommendations */}
            {section === 'recommendations' && (
              <Box>
                <Box sx={{ mb: { xs: 2, sm: 2.5 }, px: { xs: 0.5, sm: 1 } }}>
                  <SectionHeading title={t('hc_section_02_title')} info={t('hc_section_02_info')} />
                  {/* The routine paragraph introduces the list */}
                  {report.recommendations_routine && (
                    <Typography lang={language} sx={{ mt: 1, fontSize: { xs: '0.98rem', sm: '1.05rem' }, color: T.bodyStrong, lineHeight: 1.7, whiteSpace: 'pre-line', textAlign: 'justify', hyphens: 'auto' }}>
                      {report.recommendations_routine}
                    </Typography>
                  )}
                </Box>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: { xs: 1.5, sm: 2 } }}>
                  {sortedRecs.map((rec, i) => (
                    <RecommendationCard key={i} rec={rec} index={i} animIndex={i} mechanisms={report.mechanisms} />
                  ))}
                </Box>
              </Box>
            )}

            {/* PART 3 — Talk to Your Doctor */}
            {section === 'doctor' && (
              <Box>
                {/* Insets match the Paper's own border + padding below (p: xs 3 / md 4),
                    so the title and info icon line up with the card content. */}
                <Box
                  sx={{
                    width: '100%', pl: { xs: '29px', md: '37px' }, pr: { xs: '24px', md: '32px' },
                    boxSizing: 'border-box',
                  }}
                >
                  <SectionLabel
                    index="03"
                    title={t('hc_section_03_title')}
                    info={t('hc_section_03_info')}
                  />
                </Box>
                <Paper
                  elevation={0}
                  data-tour="arzt-sprechen"
                  sx={{
                    p: { xs: 3, md: 4 },
                    borderRadius: '18px',
                    bgcolor: T.dark ? alpha('#fff', 0.04) : alpha('#fff', 0.5),
                    backdropFilter: 'blur(28px) saturate(180%)',
                    WebkitBackdropFilter: 'blur(28px) saturate(180%)',
                    border: `1px solid ${T.dark ? 'rgba(255,255,255,0.08)' : 'rgba(255,255,255,0.65)'}`,
                    boxShadow: T.dark
                      ? 'inset 0 1px 0 rgba(255,255,255,0.06), 0 8px 32px rgba(0,0,0,0.55)'
                      : 'inset 0 1px 0 rgba(255,255,255,0.7), 0 4px 24px rgba(0,0,0,0.06)',
                    transition: 'box-shadow 0.25s, transform 0.22s',
                    '&:hover': {
                      transform: 'translateY(-3px)',
                      boxShadow: T.dark
                        ? `inset 0 1px 0 rgba(255,255,255,0.06), 0 14px 36px ${alpha(theme.palette.tertiary.C300, 0.3)}`
                        : `inset 0 1px 0 rgba(255,255,255,0.7), 0 14px 36px ${alpha(theme.palette.tertiary.C300, 0.22)}`,
                    },
                  }}
                >
                  {report.doctor_notes.map((note, i) => (
                    <DoctorNoteCard key={i} note={note} index={i} animIndex={i} />
                  ))}
                </Paper>
              </Box>
            )}

          </Box>
        ) : pendingCard}

      </Box>
    </>
  );
}